import { createBibleSelectorState } from "../managers/BibleSelectorManager";
import type { BibleSelectorState } from "../managers/BibleSelectorManager";
import {
  createBibleDataManager,
  type BibleDataManager,
  type BookId,
  type TranslationsCache,
  type VerseRef,
} from "../managers/BibleDataManager";
import {
  resolveTranslationUiLanguage,
  type BibleReadingState,
} from "../managers/BibleReadingManager";
import { buildReadingPath, parseReadingPath } from "../managers/ReadingUrlPath";
import {
  TODAY_PANE_ID,
  createTodayManager,
  openTodayPassage,
  todayWillAutoOpenForUrl,
  type TodayManager,
  type TodayPassageTarget,
} from "../managers/TodayManager";
import { isMinimalEmbedUrl } from "../managers/EmbedMode";
import { TodayPane, TodayPaneTitle } from "../components/TodayPane/TodayPane";
import { openExtensionConsentModal } from "../components/ExtensionConsentModal/ExtensionConsentModal";
import { AboutPage, AboutPaneTitle } from "../components/AboutPage/AboutPage";
import {
  SharedPageLoadFailedModalContent,
  SharedPageModalContent,
  SharedPageNotFoundModalContent,
  type SharedPageModalAction,
} from "../components/SharedPageModal/SharedPageModal";
import {
  buildSharedPagePath,
  parseSharedPagePath,
  type SharedPageKind,
} from "../managers/SharedPagePath";
import type { PlaylistPageSeed } from "../managers/PlaylistManager";
import {
  averageSessionMinutes,
  type ReadingPlanPageSeed,
  type ReadingPlanProgress,
} from "../managers/ReadingPlansManager";
import {
  buildStaticPagePath,
  parseStaticPagePath,
} from "../managers/StaticPagePath";
import {
  PROFILE_PANE_ID,
  ProfileBackButton,
  ProfilePane,
  ProfilePaneTitle,
} from "../components/ProfilePane/ProfilePane";
import {
  EDIT_PROFILE_PANE_ID,
  EditProfilePane,
  EditProfilePaneTitle,
} from "../components/ProfilePane/EditProfilePane";
import { openProfilePictureModal } from "../components/ProfilePictureModal/openProfilePictureModal";
import { showReadingPlanDetailView } from "../components/ReadingPlansPane/ReadingPlansPane";
import {
  YOUR_CONTENT_PANE_ID,
  YourContentPane,
  YourContentPaneTitle,
} from "../components/YourContentPane/YourContentPane";
import {
  buildCustomizationTutorialSteps,
  openCustomizationEditPane,
} from "../components/CustomizationEditPane/CustomizationEditPane";
import {
  PLAYLIST_HISTORY_PANE_ID,
  PlaylistHistoryPane,
  PlaylistHistoryPaneTitle,
} from "../components/PlaylistHistoryPane/PlaylistHistoryPane";
import {
  openReadingPlanDetail,
  openReadingPlanEditor,
} from "../components/ReadingPlansPane/ReadingPlansPane";
import {
  FRIENDS_PANE_ID,
  FriendsPane,
  FriendsPaneTitle,
} from "../components/FriendsPane/FriendsPane";
import { setupFriendLinks } from "../components/FriendsPane/FriendLinkPrompts";
import {
  EXTENSIONS_PANE_ID,
  ExtensionsPage,
  ExtensionsPaneBackButton,
  ExtensionsPaneTitle,
} from "../components/ExtensionsPage/ExtensionsPage";
import {
  createYourContentManager,
  type YourContentManager,
} from "../managers/YourContentManager";
import {
  META_DESCRIPTION_MAX_GRAPHEMES,
  buildChapterExcerpt,
  countGraphemes,
  truncateForMeta,
} from "../managers/ChapterText";
import type { OfflineTranslationStore } from "../managers/OfflineTranslationStore";
import {
  createBibleToolsManager,
  openReadingPlansPane,
  READING_PLANS_PANE_ID,
} from "../managers/BibleToolsManager";
import type { ToolsManager } from "../managers/BibleToolsManager";
import {
  type ApiResponseCache,
  FreeUseBibleAPI,
  getDefaultAPIEndpoint,
  type TranslationBook,
} from "../managers/FreeUseBibleAPI";
import { createPanes } from "../managers/PanesManager";
import type { Pane, PanesManager } from "../managers/PanesManager";
import { createTabsLayout } from "../managers/TabsLayoutManager";
import type {
  TabSlot,
  TabSlotLayoutId,
  TabsLayoutManager,
} from "../managers/TabsLayoutManager";
import { createLoginManager } from "../managers/LoginManager";
import type { LoginManager } from "../managers/LoginManager";
import { createSidebar } from "../managers/SidebarManager";
import { createTabs, readInitialReadingParams } from "../managers/TabsManager";
import type { ReaderTab, TabsManager } from "../managers/TabsManager";
import {
  writeStoredTabsState,
  type PersistedTab,
} from "../managers/TabsPersistence";
import {
  generateThemeCssVariables,
  createTheme,
  generateThemeCssClasses,
} from "../managers/ThemeManager";
import type {
  ThemeManager,
  ThemeFontFamilyKey,
} from "../managers/ThemeManager";
import {
  createCustomizationsManager,
  CUSTOMIZATION_FONT_FIELDS,
  CUSTOMIZATION_FONT_PRESETS,
} from "../managers/CustomizationsManager";
import type {
  CustomizationsManager,
  InitialCustomizationSeed,
} from "../managers/CustomizationsManager";
import { createCustomizationVariantSelectionsManager } from "../managers/CustomizationVariantSelectionsManager";
import { createCustomizationExtensionPreferencesManager } from "../managers/CustomizationExtensionPreferencesManager";
import {
  batch,
  computed,
  effect,
  signal,
  type ReadonlySignal,
} from "@preact/signals";
import {
  createReadingHistoryManager,
  type ReadingHistoryManager,
} from "../managers/ReadingHistoryManager";
import {
  createExtensionManager,
  setupExtensionContext,
  type ExtensionManager,
} from "../managers/ExtensionManager";
import {
  createExtensionSettingsManager,
  type ExtensionSettingsManager,
} from "../managers/ExtensionSettingsManager";
import {
  createHighlightsManager,
  type HighlightsManager,
} from "../managers/HighlightsManager";
import {
  createSavesManager,
  type SavesManager,
} from "../managers/SavesManager";
import {
  createBookmarksManager,
  type BookmarksManager,
} from "../managers/BookmarksManager";
import {
  createFriendsManager,
  type FriendsManager,
} from "../managers/FriendsManager";
import { createFriendReadLimiter } from "./friendContentFreshness";
import {
  createChatsManager,
  type ChatSession,
  type ChatsManager,
} from "./ChatsManager";
import {
  createSessionsManager,
  isSessionHost,
  type BibleReadingSession,
  type SessionsManager,
  type SessionStartPosition,
} from "../managers/SessionsManager";
import {
  createAnnotationsManager,
  type Annotation,
  type AnnotationsManager,
} from "../managers/AnnotationsManager";
import { syncAnnotationConflictModal } from "../components/AnnotationConflictModal/AnnotationConflictModal";
import { createAdoptionPrompt } from "../components/AdoptDeviceContentModal/AdoptDeviceContentModal";
import {
  createModalManager,
  type ModalManager,
} from "../managers/ModalManager";
import {
  createSettings,
  type SettingsManager,
} from "../managers/SettingsManager";
import {
  createInvitationsManager,
  type InvitationsManager,
} from "../managers/InvitationsManager";
import { createSearchManager } from "../managers/SearchManager";
import {
  createNavigationManager,
  type NavigationManager,
} from "../managers/NavigationManager";
import { CasualOSManager } from "./OsManager";
import { type AppConfig } from "../app/appConfig";
import {
  createI18nManager,
  getBrandedAppText,
  type I18nManager,
} from "../i18n";
import {
  createOnboardingManager,
  type OnboardingManager,
} from "../managers/OnboardingManager";
import {
  createTutorialManager,
  parseTutorialLink,
  mirrorTutorialToUrl,
  type TutorialManager,
} from "../managers/TutorialManager";
import { range } from "es-toolkit";
import {
  createReadingPlansManager,
  type ReadingPlanMetadata,
  type ReadingPlan,
  type ReadingPlansManager,
} from "../managers/ReadingPlansManager";
import {
  createDiscoverManager,
  type DiscoverManager,
} from "../managers/DiscoverManager";
import {
  createBibleReadingExtensionManager,
  type BibleReadingExtensionManager,
} from "../managers/BibleReadingExtensionManager";

type SidebarManager = ReturnType<typeof createSidebar>;
type SearchManager = ReturnType<typeof createSearchManager>;

/**
 * App-wide mobile breakpoint, in pixels. Viewports at or below this width use
 * the mobile layout (drawer sidebar, mobile header, full-screen selector);
 * above it the docked desktop layout applies. This is the single source of
 * truth for the JS side — the matching `@media (max-width: 480px)` /
 * `(min-width: 481px)` rules in components/Tabs/Tabs.css must be kept in sync by hand.
 */
export const MOBILE_BREAKPOINT = 480;

/**
 * Upper bound of the "compact desktop" band. For viewports above
 * {@link MOBILE_BREAKPOINT} but at or below this width the screen is too narrow
 * to dock a 320px sidebar beside the reader, so an *expanded* sidebar floats
 * over the reader instead of splitting the layout row. Kept in sync with the
 * matching `@media (min-width: 481px) and (max-width: 768px)` rules in
 * components/Tabs/Tabs.css by hand.
 */
export const SIDEBAR_OVERLAY_MAX_WIDTH = 768;

/**
 * Fallback `<meta name="description">` for pages with no chapter to quote —
 * the site root, and the three cases where a chapter never arrives (an upstream
 * failure, a book absent from the translation, or the SSR load timeout).
 */
const APP_META_DESCRIPTION =
  "Read, search, and study the Bible online. Free translations in many languages, with highlights, notes, bookmarks, and reading plans.";

/** Pane id for the "/{lang}/about" page's fullscreen pane (see `isAboutPage`). */
export const ABOUT_PANE_ID = "about-page-pane";
export const SHARED_PAGE_MODAL_ID = "shared-page";

/**
 * Derived app-level state and high-level actions used by UI components.
 *
 * These values are mostly computed from lower-level managers and represent
 * the currently active reading context and pane selection.
 */
export interface ToastOptions {
  /** Makes the toast tappable. Tapping it dismisses the toast first. */
  onClick?: () => void;
  /** Secondary line telling people what tapping the toast does. */
  hint?: string;
  /**
   * Marks toasts that describe the same state, so a newer one replaces an
   * older one still waiting in the queue.
   */
  key?: string;
}

export interface AppToast extends ToastOptions {
  id: number;
  message: string;
}

export interface AppState {
  /** True when multi-slot tab layouts are enabled by config. */
  panelsEnabled: ReadonlySignal<boolean>;
  /** Currently selected reading tab, or null when no tab is available. */
  selectedTab: ReadonlySignal<ReaderTab | null>;
  /**
   * Effective tab slot list shown by the UI (single-slot fallback when panels
   * are disabled, and always a single slot on mobile).
   */
  effectiveSlots: ReadonlySignal<TabSlot[]>;
  /**
   * Effective tab slot layout the UI should render. Mirrors the tabs layout
   * manager's layout on desktop, but on mobile it is always coerced to
   * `single` (one reader fills the viewport) without mutating the manager's
   * stored layout.
   */
  effectiveSlotLayout: ReadonlySignal<TabSlotLayoutId>;
  /**
   * Effective custom-pane list shown by the UI — identical to
   * `panes.panes` on desktop, but on mobile every pane is remapped to
   * `"fullscreen"` placement for rendering only (the manager's stored
   * placement is left untouched).
   */
  effectivePanes: ReadonlySignal<Pane[]>;

  /** Current window inner width in pixels. Updated on resize. */
  viewportWidth: ReadonlySignal<number>;
  /** Current window inner height in pixels. Updated on resize. */
  viewportHeight: ReadonlySignal<number>;
  /**
   * Re-reads the real `window.innerWidth`/`.innerHeight` and applies them.
   * Call once from a post-mount effect to correct the SSR-matched seed value
   * (see `viewportWidth`/`viewportHeight`) to the device's real viewport
   * right after Preact's first commit. No-op on the server.
   */
  applyViewport: () => void;

  /**
   * Applies every value the app keeps in `localStorage` that also feeds the
   * first render — saved tabs and their slot layout, the cached translation
   * catalog, the selector's view mode, and the tutorial/onboarding flags.
   *
   * All of those seed to what the server rendered (it has no `localStorage` at
   * all), because a client tree with extra elements is the one hydration
   * divergence Preact reports rather than silently patching. Call once from a
   * post-mount effect — see `MainBody` in `app/main.tsx` — so the device's real
   * state arrives as a normal diffed re-render. Ordering matters and is fixed
   * here: slots bind to tab objects by id, so tabs are restored first.
   */
  hydrateFromStorage: () => void;

  /** True when viewport width is at or below the mobile breakpoint (480px). */
  isMobile: ReadonlySignal<boolean>;
  /**
   * Compact partner-site embed (`?embed=minimal` or `?embed=true`). The
   * reader keeps a phone-like chrome and drops Today, the bottom tab bar,
   * and the sidebar so the passage is easy to read inside an iframe.
   */
  isMinimalEmbed: ReadonlySignal<boolean>;
  /**
   * Phone layout, or a compact embed. Drives the swipe reader, mobile
   * header, and floating chapter nav — not pane placement or the sidebar.
   */
  isCompactReader: ReadonlySignal<boolean>;
  /** True when on a phone-sized viewport held in landscape orientation. */
  isMobileLandscape: ReadonlySignal<boolean>;
  /**
   * True in the "compact desktop" band (just above the mobile breakpoint) where
   * an expanded sidebar floats over the reader as an overlay rather than
   * docking beside it.
   */
  isCompactDesktop: ReadonlySignal<boolean>;

  /**
   * Snapshot of the current chapter selection for analytics and integrations.
   * Null when there is no active tab/chapter.
   */
  currentReadingState: ReadonlySignal<{
    tab: ReaderTab;

    translationId: string | null;
    bookId: string | null;
    chapterNumber: number | null;
  } | null>;

  /** Selects a tab and synchronizes slot focus. */
  selectTab: (tabId: string) => void;
  /** Creates a new tab and selects it. */
  addTab: () => void;
  /** Opens an existing tab in a new tab slot. */
  openInNewSlot: (tabId: string) => void;
  /** Selects a tab slot and updates related UI state. */
  selectSlot: (slotId: string) => void;
  /** Selects a custom pane. */
  selectPane: (paneId: string) => void;
  /** Closes any pane filling the reader. */
  closeFullscreenPanes: () => void;
  /**
   * Creates a shared reading session and opens it in a new tab, starting from
   * the active tab's translation, book and chapter.
   */
  createSharedSession: () => Promise<BibleReadingSession>;
  /** Joins an existing shared session and opens it in a new tab. */
  joinSharedSession: (id: string) => Promise<BibleReadingSession>;

  /**
   * The Canonical URL for the current page.
   * Origin-relative, and always the explicit four-segment reading path
   * (e.g. `/en/AAB/genesis/1`) — see the computed for why the language segment
   * is always spelled out and why it follows the translation.
   */
  canonicalUrl: ReadonlySignal<string>;

  /** The title of the page. */
  title: ReadonlySignal<string>;

  /** The description of the page. */
  description: ReadonlySignal<string>;

  /** The social title of the page (used for Open Graph and other social media metadata). */
  socialTitle: ReadonlySignal<string>;

  /** The name of the site (used for Open Graph and other social media metadata). */
  siteName: ReadonlySignal<string>;

  /**
   * The active customization's uploaded logo, used as this page's favicon and
   * its `og:image`/`og:image:alt` social-preview image (see entry-ssr.tsx's
   * `<link rel="icon">` and meta block). Null when no customization is active
   * or the active one hasn't uploaded a logo — either way, the page keeps the
   * defaults already in index.html.
   */
  customizationLogoUrl: ReadonlySignal<string | null>;

  /**
   * The `og:image` this page should advertise instead of index.html's
   * default: a shared playlist's cover, else the active customization's logo.
   * Null keeps the default.
   */
  socialImage: ReadonlySignal<{ url: string; alt: string } | null>;

  /** Whether the current URL is the static "/{lang}/about" page. */
  isAboutPage: ReadonlySignal<boolean>;

  /**
   * Starts the shared playlist or reading plan the current page shows (its
   * modal's Start button) and leaves the page for the reader. No-op off one.
   */
  startSharedPage: () => void;

  /**
   * Resumes the reading plan the current page shows where the signed-in
   * reader left off, in the plans pane. No-op when they haven't started it.
   */
  resumeSharedPage: () => void;

  /** The toast currently shown at the bottom of the screen, or null when none. */
  currentToast: ReadonlySignal<AppToast | null>;
  /**
   * Shows a toast message at the bottom of the screen. Toasts are queued and
   * shown one at a time. A toast with nothing waiting behind it stays 3.5 s;
   * once another is waiting, the current one stays only until it has been
   * visible 1.5 s (or leaves at once if it already has). A toast with an
   * `onClick` always stays 6 s, waiting or not, so there's time to tap it;
   * tapping it dismisses it and moves on to the next one.
   * Toasts sharing a `key` describe the same state, so a new one drops any
   * waiting toast with that key and joins the end of the queue; the toast on
   * screen is never dropped. If the toast on screen already has the same key and
   * message, the new one is not queued at all. Toasts without a key are always
   * shown.
   */
  toast: (message: string, options?: ToastOptions) => void;

  /** Opens a chat session. */
  openChat: (sharedChat: ChatSession) => void;

  /** Opens a verse reference. */
  openVerseReference: (ref: VerseRef) => Promise<void>;

  /** Whether the Discover panel is currently open. */
  isDiscoverOpen: ReadonlySignal<boolean>;

  /**
   * Toggles the Discover panel open/closed.
   */
  openDiscover: () => void;

  /** Closes the Discover panel. */
  closeDiscover: () => void;
}

/**
 * Root state container for Seed Bible.
 *
 * This object aggregates all domain managers plus app-level computed state so
 * components can consume one consistent source of truth.
 */
export interface SeedBibleState {
  os: CasualOSManager;

  /** Bible API and translation/chapter data orchestration. */
  bibleData: BibleDataManager;
  /**
   * Theme manager plus derived CSS variables/classes for rendering the
   * Customization-blended theme (see `theme` below). `ThemeManager` itself
   * still writes the unblended preset+settings theme straight to
   * `document.head` — see `ThemeManager.tsx` — these extra fields are what
   * layers the active Customization on top of that in the component tree.
   */
  theme: ThemeManager & {
    themeCssVariables: ReadonlySignal<string>;
    themeCssClasses: ReadonlySignal<string>;
    /** Google Font family names an active customization's variant needs that aren't already statically loaded. */
    googleFontFamiliesToLoad: ReadonlySignal<string[]>;
  };
  /** Saved, named "look and feel" color profiles the user can create/activate. */
  customizations: CustomizationsManager;
  /** Sidebar/settings visibility manager. */
  sidebar: SidebarManager;
  /** Reader tab lifecycle manager. */
  tabs: TabsManager;
  /** Tab slot layout manager — Bible reading content always lives here. */
  tabsLayout: TabsLayoutManager;
  /** Custom (non-tab) pane manager: fullscreen/side/floating panes. */
  panes: PanesManager;
  /** Bible selector state for book/chapter picking. */
  selector: BibleSelectorState;
  /** Dynamic tool registry used by reader panes/toolbars. */
  tools: ToolsManager;
  /** Authentication and user profile manager. */
  login: LoginManager;
  /** Reading history persistence and sync manager. */
  readingHistory: ReadingHistoryManager;
  /** Verse highlight manager. */
  highlights: HighlightsManager;
  /** Archival saves manager: categorized references to chapters and verses. */
  saves: SavesManager;
  /** Named, colored chapter markers the user moves as they read. */
  bookmarks: BookmarksManager;
  /**
   * The signed-in user's friends and pending friend requests. A friendship is
   * a mutual shared permission; for now it only decides whose already-public
   * highlights, notes, playlists, reading plans and reading history are shown.
   */
  friends: FriendsManager;
  /** Annotation manager for notes/metadata. */
  annotations: AnnotationsManager;
  /** Chat session manager for in-app chat state. */
  chats: ChatsManager;
  /** Shared reading sessions manager. */
  sessions: SessionsManager;
  /** Modal manager for app-wide dialog state and rendering. */
  modals: ModalManager;
  /** App-level settings: font size, layout, book orientation, UI size, selection UI, etc. */
  settings: SettingsManager;
  /** Incoming session invitations and invite-sending. */
  invitations: InvitationsManager;
  /** Search manager for Typesense-backed queries. */
  search: SearchManager;
  /** First-run onboarding flow (welcome + install-to-home-screen prompt). */
  onboarding: OnboardingManager;
  /** Guided coachmark tour of the main UI. */
  tutorial: TutorialManager;
  /** In-app URL/state navigation manager for same-document routing. */
  navigation: NavigationManager;
  /**
   * Internationalization manager: current language, translation function, etc.
   */
  i18n: I18nManager;
  /** Reading plans: authoring, progress, and calendar. */
  readingPlans: ReadingPlansManager;
  /** Discover manager for contextual content providers. */
  discover: DiscoverManager;
  /** The Today screen: its open state and the data its cards read. */
  today: TodayManager;
  /**
   * Registry of reading extensions that can be enabled per reading state to
   * enhance navigation, discovered content, and session-synced custom data.
   */
  readingExtensions: BibleReadingExtensionManager;
  /**
   * Playlist manager for creating, editing, and syncing user playlists.
   */
  playlists: PlaylistManager;
  /**
   * Reads scripture aloud with the browser's own speech synthesiser, for the
   * translations that ship no recorded narration.
   */
  textToSpeech: TextToSpeechManager;
  /**
   * The audio the reader is currently showing playback controls for (e.g. a
   * chapter's narration), so its toolbars can display progress and scrub.
   */
  audioPlayback: AudioPlaybackManager;
  /** Saved photos the user has uploaded, for reuse as covers and later features. */
  gallery: UserGalleryManager;
  /** Aggregated computed app state and top-level UI actions. */
  app: AppState;
  /** Extension loading and runtime manager. */
  extensions: ExtensionManager;
  /** Per-viewer values for extensions' declared settings, resolved against the active Customization's defaults. */
  extensionSettings: ExtensionSettingsManager;

  /**
   * Feature flag manager for enabling/disabling features at runtime.
   */
  features: FeaturesManager;

  /** Backs the "Your content" screen: the user's own annotations/highlights. */
  yourContent: YourContentManager;

  /** True when the "Your content" screen is showing. */
  isYourContentOpen: ReadonlySignal<boolean>;
  /** Opens "Your content" (reflected in the URL as `?content=open`). */
  openYourContent: () => void;
  /** Closes "Your content" (clears `content` from the URL). */
  closeYourContent: () => void;

  /** True when the playlist history screen is showing. */
  isPlaylistHistoryOpen: ReadonlySignal<boolean>;
  /** Opens playlist history (reflected in the URL as `?playlist-history=open`). */
  openPlaylistHistory: () => void;
  /** Closes playlist history (clears `playlist-history` from the URL). */
  closePlaylistHistory: () => void;

  /** True when the Friends screen is showing. */
  isFriendsOpen: ReadonlySignal<boolean>;
  /** Opens Friends (reflected in the URL as `?friends=open`). */
  openFriends: () => void;
  /** Closes Friends (clears `friends` from the URL). */
  closeFriends: () => void;

  /** True when the full-screen Extensions page is showing (desktop). */
  isExtensionsOpen: ReadonlySignal<boolean>;
  /**
   * The extension whose page is open on the Extensions page, or null while
   * it lists them all.
   */
  extensionsPageEntryId: ReadonlySignal<string | null>;
  /**
   * Opens the Extensions page, at `extensionId`'s page when given (reflected
   * in the URL as `?extensions=open&extension=<id>`).
   */
  openExtensions: (extensionId?: string | null) => void;
  /** Closes the Extensions page (clears `extensions` and `extension`). */
  closeExtensions: () => void;

  /** True when the Profile screen is showing. */
  isProfileOpen: ReadonlySignal<boolean>;
  /** Opens the Profile screen (reflected in the URL as `?profile=open`). */
  openProfile: () => void;
  /** Closes the Profile screen (clears `profile` from the URL). */
  closeProfile: () => void;

  /** True when the "Edit profile" screen is showing. */
  isEditProfileOpen: ReadonlySignal<boolean>;
  /** Opens "Edit profile" (reflected in the URL as `?edit-profile=open`). */
  openEditProfile: () => void;
  /** Closes "Edit profile" (clears `edit-profile` from the URL). */
  closeEditProfile: () => void;

  /** True when the Terms of Service modal is open. */
  isTermsOpen: ReadonlySignal<boolean>;
  /** Opens the Terms of Service modal (reflected in the URL as `?terms=open`). */
  openTerms: () => void;
  /** Closes the Terms of Service modal (clears `terms` from the URL). */
  closeTerms: () => void;

  /** True when the Privacy Policy modal is open. */
  isPrivacyOpen: ReadonlySignal<boolean>;
  /** Opens the Privacy Policy modal (reflected in the URL as `?privacy=open`). */
  openPrivacy: () => void;
  /** Closes the Privacy Policy modal (clears `privacy` from the URL). */
  closePrivacy: () => void;

  /** True when the Code of Conduct modal is open. */
  isCodeOfConductOpen: ReadonlySignal<boolean>;
  /** Opens the Code of Conduct modal (reflected in the URL as `?conduct=open`). */
  openCodeOfConduct: () => void;
  /** Closes the Code of Conduct modal (clears `conduct` from the URL). */
  closeCodeOfConduct: () => void;

  /**
   * Releases what this state has registered outside itself (listeners on the
   * process-wide i18next instance, recurring timers), so it can be garbage
   * collected. The SSR host creates one state per request and must call this
   * once the render is done; the client keeps its one state for the page's
   * lifetime and never needs to.
   */
  dispose: () => void;
}

// Evaluated before the extension bundle below. Bonfire imports
// `formatAvailableTranslationsNote` from this module while that bundle is
// still loading, and a live binding from a half-finished barrel would be
// unset if this import ran afterwards.
import { createTranslationAgentTools } from "./translationSearch";

// The extension set is auto-discovered from every extension package under
// `packages/` by the `vite-plugin-extensions` plugin. See
// script/lib/vite-plugin-extensions.ts.
import SEED_BIBLE_EXTENSIONS from "virtual:@extensions";
import {
  createPlaylistManager,
  playlistHistoryOpenFailedMessage,
  type PlaylistManager,
  type PlaylistItemData,
  type Playlist,
  type PlaylistPlayHistory,
} from "./PlaylistManager";
import {
  createUserGalleryManager,
  type UserGalleryManager,
} from "./UserGalleryManager";
import {
  createTextToSpeechManager,
  type TextToSpeechManager,
} from "./TextToSpeechManager";
import {
  createAudioPlaybackManager,
  type AudioPlaybackManager,
} from "./AudioPlaybackManager";
import { createFeaturesManager, type FeaturesManager } from "./FeaturesManager";
import {
  DiscoverPane,
  DiscoverPaneHeader,
  DiscoverPaneTitle,
} from "../components/DiscoverPane/DiscoverPane";
import {
  AIBibleVerseRefSchema,
  convertToPlaylistItem,
  GeneratedPlaylistSchema,
  generateFunctionTool,
} from "./AIManager";
import { z } from "zod";
import { getDefaultTranslationForLanguage } from "./BibleReadingManager";
import { captureEvent } from "./Utils";

/**
 * Creates and wires the full Seed Bible application state graph.
 *
 * Manager dependencies are initialized in order, then composed into derived
 * signals/actions that power the UI. The resulting state is also passed to
 * extension context setup.
 */
export interface CreateSeedBibleStateOptions {
  /** Deployment config (base path + asset host). */
  config?: AppConfig;
  /** Full initial URL — supplied during SSR where `window` is unavailable. */
  initialHref?: string;
  /**
   * Where translations downloaded for offline reading are stored. Defaults to
   * IndexedDB; tests pass an in-memory store, and null disables the feature.
   */
  offlineStore?: OfflineTranslationStore | null;
  /**
   * Cache shared across `getTranslations()` calls. Only the SSR host passes
   * one in (see `standalone/ssrTranslationsCache.ts`) — omitted here, client
   * behavior (per-page-load cache + localStorage) is unchanged.
   */
  translationsCache?: TranslationsCache;
  /**
   * Process-wide cache of chapter/book-catalog requests, SSR host only —
   * see `standalone/ssrApiResponseCache.ts`.
   */
  apiResponseCache?: ApiResponseCache;
  /**
   * A `FreeUseBibleAPI.snapshotResponseCache()` snapshot to seed the new
   * `FreeUseBibleAPI` instance with, so it doesn't refetch data another
   * instance already fetched. The client uses this to seed its own API cache
   * with whatever the server already fetched for the SSR render — see
   * `readInjectedApiResponseSnapshot` in `app/apiResponseSeed.ts`.
   */
  apiResponseSnapshot?: Record<string, unknown>;

  /**
   * A prior SSR render's completed `?customization=...` load, so the new
   * `CustomizationsManager` doesn't re-fetch a record the server already
   * resolved. The client uses this to seed its own load with whatever the
   * server already fetched for the SSR render — see
   * `readInjectedCustomizationSeed` in `app/customizationSeed.ts`.
   */
  initialCustomizationSeed?: InitialCustomizationSeed;

  /**
   * True when `initialCustomizationSeed` came from a server running a
   * different build: it's still applied for the first paint, but the record
   * is downloaded again, since an older build drops fields it doesn't know.
   */
  revalidateInitialCustomizationSeed?: boolean;

  /**
   * A prior SSR render's completed playlist-page load, so the new
   * `PlaylistManager` doesn't re-fetch it — see `readInjectedPlaylistPageSeed`
   * in `app/playlistPageSeed.ts`.
   */
  initialPlaylistPageSeed?: PlaylistPageSeed;

  /** Same as `initialPlaylistPageSeed`, for a shared reading plan page. */
  initialReadingPlanPageSeed?: ReadingPlanPageSeed;
}

/** Where a shared session started from this reading surface should open. */
function getSessionStartPosition(
  readingState: BibleReadingState
): SessionStartPosition {
  return {
    initialTranslationId: readingState.translationId.value,
    initialBookId: readingState.bookId.value,
    initialChapterNumber: readingState.chapterNumber.value,
  };
}

export function createSeedBibleState(
  options: CreateSeedBibleStateOptions = {}
): SeedBibleState {
  console.log("Creating SeedBibleState with options:", options);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const features = createFeaturesManager((globalThis as any).posthog ?? null);
  const navigation = createNavigationManager({
    initialHref: options.initialHref,
    basePath: options.config?.basePath,
  });
  const branding = options.config?.branding;
  const brandingThemes = branding?.whiteLabelThemes;
  const api = new FreeUseBibleAPI(
    getDefaultAPIEndpoint(navigation.currentUrl.value),
    options.apiResponseCache
  );
  if (options.apiResponseSnapshot) {
    api.seedResponseCache(options.apiResponseSnapshot);
  }
  const i18n = createI18nManager(
    navigation,
    options.config?.acceptedLanguages ?? []
  );
  const data = createBibleDataManager(api, {
    offlineStore: options.offlineStore,
    translationsCache: options.translationsCache,
  });
  const os = CasualOSManager();
  const login = createLoginManager({ os });
  const modals = createModalManager();
  // Both managers ask through the same prompt, so one sign-in raises one
  // dialog even when the device holds highlights and notes.
  const askToAdopt = createAdoptionPrompt(modals);
  // One limit on reading friends' content, shared by every manager that does.
  const friendReads = createFriendReadLimiter();
  const highlights = createHighlightsManager(os, login, {
    confirmAdoption: (owner) => askToAdopt(owner, "highlights"),
    friendReads,
  });
  const saves = createSavesManager(os, login);
  const bookmarks = createBookmarksManager(os, login);
  // The saves and bookmarks panels occupy the same spot in the sidebar, so
  // opening either one closes the other.
  effect(() => {
    if (bookmarks.isPanelOpen.value) {
      saves.isFilterActive.value = false;
    }
  });
  effect(() => {
    if (saves.isFilterActive.value) {
      bookmarks.isPanelOpen.value = false;
    }
  });
  const friends = createFriendsManager(os, login);
  const settings = createSettings(os, login, navigation);
  // Persist a user's explicit language selection to their profile. Wiring it
  // through `requestLanguageChange` (rather than a blanket `languageChanged`
  // listener) keeps URL-driven language changes view-only.
  i18n.setLanguagePersister(settings.persistLanguage);

  // "Never ask again" on the UI-language switch prompt is an ordinary setting,
  // so it rides on the same profile-when-logged-in / device-when-not storage
  // as every other one.
  i18n.setUiLanguagePromptPreference({
    isEnabled: () => settings.settings.value.askToSwitchUiLanguage,
    disable: () => settings.setAskToSwitchUiLanguage(false),
  });

  const panelsEnabled = computed(() => !settings.settings.value.disablePanels);
  const themeManager = createTheme(settings, brandingThemes);
  const customizationVariantSelections =
    createCustomizationVariantSelectionsManager(os, login);
  const customizationExtensionPreferences =
    createCustomizationExtensionPreferencesManager(os, login);
  const customizations = createCustomizationsManager(
    os,
    login,
    themeManager,
    navigation,
    customizationVariantSelections,
    customizationExtensionPreferences,
    options.initialCustomizationSeed,
    options.revalidateInitialCustomizationSeed
  );
  // The active Customization's chosen default translation, if any — see
  // `createTabs`'s `activeCustomizationDefaultTranslationId` parameter for how
  // it overrides Seed Bible's per-language default the same way
  // `branding.defaultTranslationId` does for a whole deployment.
  const activeCustomizationDefaultTranslationId = computed(
    () => customizations.activeCustomization.value?.defaultTranslationId
  );
  // Filled once tabs exist so local chat can resolve localized book names.
  const selectedTabTranslationBooks = signal<TranslationBook[] | undefined>(
    undefined
  );
  const chats = createChatsManager(login, i18n, selectedTabTranslationBooks);
  const sidebar = createSidebar({ navigation, chatsManager: chats });
  const discover = createDiscoverManager();
  const readingExtensions = createBibleReadingExtensionManager();
  const tabs = createTabs(
    navigation,
    data,
    highlights,
    chats,
    i18n,
    login,
    discover,
    readingExtensions,
    () => annotations,
    branding,
    settings,
    activeCustomizationDefaultTranslationId
  );
  const tabsLayout = createTabsLayout(tabs, panelsEnabled);
  const selector = createBibleSelectorState(
    data,
    tabs,
    tabsLayout,
    settings,
    sidebar,
    saves,
    navigation,
    login,
    i18n
  );
  const tools = createBibleToolsManager(branding);
  const readingHistory = createReadingHistoryManager(os, login);

  const renderedAsMobile = options.config?.renderedAsMobile ?? false;

  // Seeded from the SAME `renderedAsMobile` guess the server made — never
  // `window.innerWidth`/`.innerHeight` here — so the client's first
  // render/hydrate pass produces the identical viewport-derived layout the
  // server rendered, regardless of the device's actual screen size.
  // `applyViewport` (below, exposed on `AppState`) corrects this to the real
  // dimensions once, from a post-mount effect in `MainBody` — see
  // `app/main.tsx`.
  const viewportWidth = signal(renderedAsMobile ? MOBILE_BREAKPOINT : 1000);
  const viewportHeight = signal(renderedAsMobile ? 800 : 1000);
  const isMobile = computed(() => viewportWidth.value <= MOBILE_BREAKPOINT);

  const annotationRecordKey =
    navigation.currentUrl.value.searchParams.get("annotationRecordKey") ??
    undefined;
  const annotations = createAnnotationsManager(
    os,
    login,
    tabs,
    discover,
    annotationRecordKey,
    {
      confirmAdoption: (owner) => askToAdopt(owner, "notes"),
      friendIds: friends.friendIds,
      friendReads,
    }
  );
  const yourContent = createYourContentManager({
    annotations,
    highlights,
    bibleData: data,
  });
  const sessions = createSessionsManager(
    os,
    data,
    login,
    highlights,
    i18n,
    readingExtensions,
    () => annotations
  );
  const extensions = createExtensionManager(login, {
    defaultExtensions: SEED_BIBLE_EXTENSIONS,
    requestSandboxConsent: (request) =>
      openExtensionConsentModal(modals, request, {
        userId: login.userId,
        friends,
      }),
    records: os,
    fileRecords: os,
  });
  const extensionSettings = createExtensionSettingsManager(
    os,
    login,
    extensions,
    customizations
  );
  // Swaps the live installed-extension set over to whichever customization is
  // active (its own extensionIds plus anything the viewer added for it), and
  // back to the viewer's real default profile when none is active. Guarded
  // so it stays a no-op until a customization has genuinely gone active at
  // least once this session — effect() runs synchronously on registration,
  // and without the guard this would fire on every page load and race
  // app/main.tsx's own initial `loadDefaultExtensions()` call.
  let previousExtensionTargetKey: string | null = null;
  let hasEnteredCustomizationExtensionMode = false;
  effect(() => {
    const active = customizations.activeCustomization.value;
    const targetIds = active ? customizations.activeExtensionIds.value : null;
    if (targetIds === null && !hasEnteredCustomizationExtensionMode) {
      return;
    }
    const key = targetIds === null ? " default" : [...targetIds].sort().join("");
    if (key === previousExtensionTargetKey) {
      return;
    }
    previousExtensionTargetKey = key;
    if (targetIds !== null) {
      hasEnteredCustomizationExtensionMode = true;
    }
    void extensions.reconcileInstalledExtensions(targetIds);
  });
  const search = createSearchManager();

  // When the app is opened via a content link — a shared-session invite
  // (`?sessionId=...`), a shared playlist (`?playlist=...` or a
  // `/{lang}/playlist/...` page), or a shared reading plan (`?readingPlan=...`
  // or a `/{lang}/reading-plan/...` page) — the user came to view that
  // content, not to onboard, so we skip the welcome screen and the
  // auto-starting tutorial for this visit. This is derived from the current
  // URL rather than persisted, so it only affects this tab/load: revisiting
  // without those params shows onboarding and tutorials as usual. Closing a
  // shared page without starting it lifts this (see the shared page effects
  // below).
  const openedViaSharedPage =
    typeof window !== "undefined" &&
    parseSharedPagePath(
      navigation.currentUrl.value.pathname,
      navigation.basePath
    ) !== null;
  const openedViaContentLink = signal(
    typeof window !== "undefined" &&
      (!!navigation.currentUrl.value.searchParams.get("sessionId") ||
        !!navigation.currentUrl.value.searchParams.get("playlist") ||
        !!navigation.currentUrl.value.searchParams.get("readingPlan") ||
        openedViaSharedPage)
  );

  const onboarding = createOnboardingManager(login);

  // Terms of Service modal. Two-way bound to the `?terms=open` query param so
  // it can be deep-linked: setting the param opens the modal, and closing the
  // modal clears the param. Anything other than `open` (or no param) is closed.
  const termsOpen = signal(
    navigation.currentUrl.value.searchParams.get("terms") === "open"
  );
  const isTermsOpen = computed(() => termsOpen.value);
  const openTerms = () => {
    termsOpen.value = true;
  };
  const closeTerms = () => {
    termsOpen.value = false;
  };
  // Privacy Policy modal. Two-way bound to the `?privacy=open` query param so
  // it can be deep-linked, mirroring the Terms of Service modal above.
  const privacyOpen = signal(
    navigation.currentUrl.value.searchParams.get("privacy") === "open"
  );
  const isPrivacyOpen = computed(() => privacyOpen.value);
  const openPrivacy = () => {
    privacyOpen.value = true;
  };
  const closePrivacy = () => {
    privacyOpen.value = false;
  };

  // Code of Conduct modal. Two-way bound to the `?conduct=open` query param so
  // it can be deep-linked, mirroring the modals above.
  const codeOfConductOpen = signal(
    navigation.currentUrl.value.searchParams.get("conduct") === "open"
  );
  const isCodeOfConductOpen = computed(() => codeOfConductOpen.value);
  const openCodeOfConduct = () => {
    codeOfConductOpen.value = true;
  };
  const closeCodeOfConduct = () => {
    codeOfConductOpen.value = false;
  };

  // The "Your content" screen, reached from Profile. Bound to `?content=open`
  // on the same terms as the Profile screen below.
  const contentOpen = signal(
    import.meta.env.SSR
      ? false
      : navigation.currentUrl.value.searchParams.get("content") === "open"
  );
  const isYourContentOpen = computed(() => contentOpen.value);
  const openYourContent = () => {
    contentOpen.value = true;
  };
  const closeYourContent = () => {
    contentOpen.value = false;
  };

  // Playlist history, reached from Profile. Bound to `?playlist-history=open`
  // on the same terms as the Profile screen below, and kept out of SSR so a
  // crawled URL doesn't serialize the signed-in history list.
  const playlistHistoryOpen = signal(
    import.meta.env.SSR
      ? false
      : navigation.currentUrl.value.searchParams.get("playlist-history") ===
          "open"
  );
  const isPlaylistHistoryOpen = computed(() => playlistHistoryOpen.value);
  const openPlaylistHistory = () => {
    playlistHistoryOpen.value = true;
  };
  const closePlaylistHistory = () => {
    playlistHistoryOpen.value = false;
  };

  // The Friends screen, reached from Profile. Bound to `?friends=open` on the
  // same terms as "Your content" above.
  const friendsOpen = signal(
    import.meta.env.SSR
      ? false
      : navigation.currentUrl.value.searchParams.get("friends") === "open"
  );
  const isFriendsOpen = computed(() => friendsOpen.value);
  const openFriends = () => {
    friendsOpen.value = true;
  };
  const closeFriends = () => {
    friendsOpen.value = false;
  };

  // The full-screen Extensions page, bound to `?extensions=open` and, while
  // one extension's page is showing, `?extension=<id>`.
  const extensionsOpen = signal(
    import.meta.env.SSR
      ? false
      : navigation.currentUrl.value.searchParams.get("extensions") === "open"
  );
  const extensionsPageEntry = signal<string | null>(
    import.meta.env.SSR
      ? null
      : navigation.currentUrl.value.searchParams.get("extension")
  );
  const isExtensionsOpen = computed(() => extensionsOpen.value);
  const extensionsPageEntryId = computed(() =>
    extensionsOpen.value ? extensionsPageEntry.value : null
  );
  const openExtensions = (extensionId: string | null = null) => {
    // Today's URL binding would otherwise turn it straight back on over this
    // page; see `openProfile` below.
    if (today.isOpen.peek()) {
      today.close();
    }
    batch(() => {
      extensionsOpen.value = true;
      extensionsPageEntry.value = extensionId;
    });
  };
  const closeExtensions = () => {
    batch(() => {
      extensionsOpen.value = false;
      extensionsPageEntry.value = null;
    });
  };

  // The Profile screen. Two-way bound to `?profile=open` so it can be
  // deep-linked and so the browser's back button leaves it, mirroring Today.
  //
  // Never open during SSR: `effectivePanes` renders fullscreen panes
  // unconditionally, so a crawled `/?profile=open` would otherwise serialize
  // the signed-in profile into the response HTML.
  const profileOpen = signal(
    import.meta.env.SSR
      ? false
      : navigation.currentUrl.value.searchParams.get("profile") === "open"
  );
  const isProfileOpen = computed(() => profileOpen.value);
  const openProfile = () => {
    // Close Today before the profile flag flips. Opening the profile pane
    // displaces Today's pane, but Today's URL binding is still `?today=open`
    // until its own effect runs — and that binding turns Today straight back
    // on, which removes the profile pane and closes the profile again. Closing
    // first drops `?today=` before `?profile=open` is written, so the profile
    // screen stays up (including over the welcome screen).
    if (today.isOpen.peek()) {
      today.close();
    }
    profileOpen.value = true;
  };
  const closeProfile = () => {
    profileOpen.value = false;
  };

  // "Edit profile", reached from the Profile screen. Kept out of SSR for the
  // same reason as Profile above: it renders the signed-in account.
  const editProfileOpen = signal(
    import.meta.env.SSR
      ? false
      : navigation.currentUrl.value.searchParams.get("edit-profile") === "open"
  );
  const isEditProfileOpen = computed(() => editProfileOpen.value);
  const openEditProfile = () => {
    editProfileOpen.value = true;
  };
  const closeEditProfile = () => {
    editProfileOpen.value = false;
  };

  navigation.syncSignalsToUrl({
    profile: {
      get value() {
        return profileOpen.value ? "open" : null;
      },
      set value(newValue) {
        profileOpen.value = newValue === "open";
      },
    },
    content: {
      get value() {
        return contentOpen.value ? "open" : null;
      },
      set value(newValue) {
        contentOpen.value = newValue === "open";
      },
    },
    "playlist-history": {
      get value() {
        return playlistHistoryOpen.value ? "open" : null;
      },
      set value(newValue) {
        playlistHistoryOpen.value = newValue === "open";
      },
    },
    friends: {
      get value() {
        return friendsOpen.value ? "open" : null;
      },
      set value(newValue) {
        friendsOpen.value = newValue === "open";
      },
    },
    extensions: {
      get value() {
        return extensionsOpen.value ? "open" : null;
      },
      set value(newValue) {
        extensionsOpen.value = newValue === "open";
      },
    },
    extension: {
      get value() {
        return extensionsOpen.value ? extensionsPageEntry.value : null;
      },
      set value(newValue) {
        extensionsPageEntry.value = newValue;
      },
    },
    "edit-profile": {
      get value() {
        return editProfileOpen.value ? "open" : null;
      },
      set value(newValue) {
        editProfileOpen.value = newValue === "open";
      },
    },
    terms: {
      get value() {
        return termsOpen.value ? "open" : null;
      },
      set value(newValue) {
        termsOpen.value = newValue === "open";
      },
    },
    privacy: {
      get value() {
        return privacyOpen.value ? "open" : null;
      },
      set value(newValue) {
        privacyOpen.value = newValue === "open";
      },
    },
    conduct: {
      get value() {
        return codeOfConductOpen.value ? "open" : null;
      },
      set value(newValue) {
        codeOfConductOpen.value = newValue === "open";
      },
    },
  });
  const readingPlans = createReadingPlansManager(os, login, tabs, navigation, {
    language: i18n.language,
    initialReadingPlanPageSeed: options.initialReadingPlanPageSeed,
    friendReads,
  });
  const gallery = createUserGalleryManager(os, login);
  const textToSpeech = createTextToSpeechManager();
  const audioPlayback = createAudioPlaybackManager();

  const { currentTheme } = themeManager;
  // While a Customization is active, its variant is rendered against its
  // own `baseTheme` preset (via `activeResolvedTheme` — see
  // `buildBibleThemeFromCustomizationTheme`), not against the viewer's own
  // theme — session-only, since that resolution is derived from in-memory
  // customization data, never written to `SettingsManager`.
  // `themeManager.currentTheme` itself (exposed as `state.theme.currentTheme`)
  // stays unblended so Display & Theme settings keep showing/editing the
  // user's actual theme, decoupled from whichever Customization is active.
  const theme = computed(
    () => customizations.activeResolvedTheme.value ?? currentTheme.value
  );
  const themeCssVariables = computed(() =>
    generateThemeCssVariables(theme.value)
  );
  const themeCssClasses = computed(() => generateThemeCssClasses(theme.value));

  // Presets (statically loaded in app/main.tsx's ExternalResourceDependencies)
  // plus the built-in themes' own defaults never need a dynamic <link> —
  // only a manually-typed custom font name does.
  const ALWAYS_LOADED_FONT_NAMES = new Set([
    ...CUSTOMIZATION_FONT_PRESETS.map((preset) => preset.name),
    "Plus Jakarta Sans", // default bookTitle/chapterHeading/verse font in the built-in themes
  ]);
  const FONT_FAMILY_KEYS: ThemeFontFamilyKey[] = CUSTOMIZATION_FONT_FIELDS.map(
    (field) => field.key
  );
  const googleFontFamiliesToLoad = computed<string[]>(() => {
    const vars = theme.value.variables;
    const names = new Set<string>();
    for (const key of FONT_FAMILY_KEYS) {
      const value = vars[key];
      const name = value?.split(",")[0]?.trim();
      if (name && !ALWAYS_LOADED_FONT_NAMES.has(name)) {
        names.add(name);
      }
    }
    return [...names];
  });

  const selectedTab = computed(
    () =>
      tabs.tabs.value.find((tab) => tab.id === tabs.selectedTabId.value) ?? null
  );

  // Keep local-chat scripture parsing in sync with the open reading tab.
  effect(() => {
    selectedTabTranslationBooks.value =
      selectedTab.value?.readingState.translationBooks.value?.books;
  });

  const isMinimalEmbed = computed(() =>
    isMinimalEmbedUrl(navigation.currentUrl.value)
  );
  const isCompactReader = computed(
    () => isMobile.value || isMinimalEmbed.value
  );

  // Created after `isMobile` so panes can enforce a single fullscreen pane:
  // on mobile every pane is displayed fullscreen, so opening one closes the
  // rest.
  const panes = createPanes(isMobile);
  const playlists = createPlaylistManager(
    os,
    login,
    tabs,
    navigation,
    isMobile,
    modals,
    i18n,
    readingExtensions,
    discover,
    chats,
    options.initialPlaylistPageSeed,
    { friendReads }
  );
  // True only while `hydrateFromStorage` below is applying the saved tab state.
  // Restoring the tabs replaces the URL-seeded boot tab, and the reader commits
  // the restored book/chapter to the address bar — a URL move that looks exactly
  // like a navigation to the effect below, but is the tail end of the same page
  // load. Without this, a returning visitor whose last chapter wasn't the boot
  // default watched Today open and then immediately close again.
  let restoringStoredState = false;

  // Close any fullscreen pane when the book/chapter in the URL path changes,
  // so navigating reveals the reader (every navigation path writes this
  // position into the path — see `commitSelectedTabToUrl` in TabsManager).
  // The first location only sets a baseline, so load-time init doesn't close a
  // pane auto-opened for the same load (e.g. Today via `?today=open`).
  //
  // `?verse` is deliberately NOT part of the location: TabsManager mirrors the
  // verse *selection* into that param, so counting it here made selecting (or
  // clearing) a verse look like a navigation — which closed the very pane the
  // user had just opened from the verse toolbar.
  //
  // The flip side is that this effect can't see a move *within* a chapter, so any
  // path that jumps to a verse and must reveal the reader has to call
  // `closeFullscreenPanes` itself rather than rely on this: verse reference links
  // (`handleOpenVerseReference` below) and the floating panels' jump actions
  // both do. Selecting a tab
  // (`handleSelectTab`) closes them too, which covers most of the remaining
  // paths incidentally.
  let lastReadingLocation: string | null = null;
  effect(() => {
    const url = navigation.currentUrl.value;
    const parsed = parseReadingPath(url.pathname, navigation.basePath);
    if (!parsed) {
      return;
    }

    const location = `${parsed.bookId ?? parsed.rawBookSegment}|${parsed.chapter}`;
    const previous = lastReadingLocation;
    lastReadingLocation = location;

    if (previous === null || previous === location) {
      return;
    }
    // A restore is not a navigation: the reader hasn't gone anywhere, the app is
    // catching the URL up to state that only arrived after the load — the saved
    // tabs from `localStorage` (see `restoringStoredState`) or the profile's
    // saved translation, which reaches this effect at all because it can fall
    // back to a different book (see `isRestoringProfileTranslation`). The
    // baseline above is updated first in both cases, so the restored position
    // becomes the position everything after it is compared against.
    if (restoringStoredState || tabs.isRestoringProfileTranslation()) {
      return;
    }
    panes.closeFullscreenPanes();
  });

  // Today opens only after mount (`hydrateAutoOpen`, so the client's first
  // render matches the server's), but the chapter can already be loaded from
  // the server's API snapshot and the tutorial offer armed before then. For
  // that gap the reader would read as visible with Welcome about to cover it,
  // so until Today's pane has opened once on a load where it will auto-open,
  // the reader doesn't count as visible.
  const todayWillAutoOpen = todayWillAutoOpenForUrl(
    navigation.initialUrl,
    navigation.basePath
  );
  const todayHasOpened = signal(false);
  effect(() => {
    if (panes.panes.value.some((pane) => pane.id === TODAY_PANE_ID)) {
      todayHasOpened.value = true;
    }
  });

  // The reader is visible when a chapter is loaded, no fullscreen pane covers
  // it (matching `isFullscreenPaneVisible` in BibleReaderToolbar — on mobile
  // any open pane covers the reader), and Today isn't about to auto-open over
  // it. `todayCoversReader.test.ts` guards both orderings.
  const readerVisible = computed<boolean>(() => {
    const chapterLoaded =
      selectedTab.value?.readingState.chapterData.value != null;
    if (!chapterLoaded) {
      return false;
    }
    const coveredByPane = panes.panes.value.some(
      (pane) => pane.placement === "fullscreen" || isMobile.value
    );
    if (coveredByPane) {
      return false;
    }
    if (todayWillAutoOpen && !todayHasOpened.value) {
      return false;
    }
    return true;
  });

  // Dialogs that cover the reader. Pop-ups opened through `modals` are only
  // part of the list: sign-in, Terms, Privacy, Code of Conduct, and the
  // onboarding cards are drawn on their own in `app/main.tsx`. A new dialog
  // that does not go through `modals` has to be added here, or the offer card
  // can show on top of it. `onboarding.step` starts at "done" and only leaves
  // it while an onboarding card is actually up, so a normal first visit is
  // not held back. `?tutorial=` links use `readerVisible` alone.
  const modalCoveringReader = computed(
    () =>
      modals.modals.value.length > 0 ||
      login.isLoginOpen.value ||
      termsOpen.value ||
      privacyOpen.value ||
      codeOfConductOpen.value ||
      onboarding.step.value !== "done"
  );

  // Client-only: SSR can't run the tour, and launching it there would put
  // the overlay in the served HTML.
  const tutorialLink = import.meta.env.SSR
    ? null
    : parseTutorialLink(navigation.currentUrl.value.searchParams);
  const tutorial = createTutorialManager(
    login,
    readerVisible,
    selector,
    isMobile,
    panes,
    sidebar,
    openedViaContentLink,
    isMinimalEmbed,
    tutorialLink,
    modalCoveringReader
  );

  if (!import.meta.env.SSR) {
    mirrorTutorialToUrl(tutorial, navigation, tutorialLink);
  }

  // Once the tutorial has been resolved (seen, skipped, declined, or opted
  // out) and the reader is visible, offer the install prompt — to any
  // not-yet-installed user, logged in or not. Deferring past the tutorial
  // means the profile has had time to load, so there's no "stale prompt"
  // concern the way there was on startup. One-shot via `installOfferChecked`.
  //
  // "No, thanks" and leaving the introduction tour before it finishes are
  // the exception: don't open install, and don't mark the offer resolved.
  // The download prompt waits on `installOfferResolved`, so it stays quiet
  // for the rest of this visit without a rule of its own. The next visit
  // starts fresh and shows install, unless that prompt was already dismissed
  // on this device (`sb-install-dismissed`). Finishing the tour still chains
  // install, then download, as before.
  //
  // `installOfferResolved` flips once that check has had its turn, whether or
  // not it showed anything — except the early-leave case above. The
  // offline-download offer waits on it so the two never stack, and so "offer
  // the download after the install prompt" holds.
  const installOfferResolved = signal(false);

  let installOfferChecked = false;
  effect(() => {
    if (installOfferChecked) {
      return;
    }
    // A visitor inside someone else's iframe should not be asked to install
    // our app. Resolve the offer so the offline-download prompt can take its
    // own turn and refuse for the same reason.
    if (isMinimalEmbed.value) {
      installOfferChecked = true;
      installOfferResolved.value = true;
      return;
    }
    if (openedViaContentLink.value) {
      return;
    }
    if (login.userId.value && login.profile.value === null) {
      return;
    }
    if (!readerVisible.value) {
      return;
    }
    const tutorialResolved =
      !tutorial.promptVisible.value &&
      !tutorial.running.value &&
      (tutorial.completed.value || tutorial.optedOut.value);
    if (!tutorialResolved) {
      return;
    }
    // Read after the tour has actually ended, in the same flush that marks
    // it seen (see `dismissPrompt` / `skip`). Leaving `installOfferResolved`
    // false is what keeps the download prompt from taking a turn this visit.
    if (tutorial.leftIntroductionEarly.value) {
      installOfferChecked = true;
      return;
    }
    installOfferChecked = true;
    if (onboarding.installAvailable.value) {
      onboarding.openInstall();
    }
    installOfferResolved.value = true;
  });

  // A phone held sideways: landscape orientation with the short viewport
  // height typical of phones. Tablets/desktops in landscape have more
  // vertical room and are excluded by the height cap.
  const isMobileLandscape = computed(
    () =>
      viewportHeight.value > 0 &&
      viewportHeight.value <= 600 &&
      viewportWidth.value > viewportHeight.value
  );

  /**
   * Re-reads the real `window.innerWidth`/`.innerHeight` and applies them.
   * Shared by the resize listener below and a one-time post-mount correction
   * (`MainBody` in `app/main.tsx`) — one place reads `window.inner*` into
   * these signals. No-op on the server.
   */
  const applyViewport = () => {
    if (typeof window === "undefined") {
      return;
    }
    batch(() => {
      viewportWidth.value = window.innerWidth;
      viewportHeight.value = window.innerHeight;
    });
  };

  effect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.addEventListener("resize", applyViewport);
    return () => {
      window.removeEventListener("resize", applyViewport);
    };
  });

  // Auto-collapse the docked sidebar when entering mobile-landscape so the
  // reader gets the limited vertical space. Reacting only to the boolean
  // means this fires once per transition into landscape and still lets the
  // user manually re-expand afterwards.
  effect(() => {
    if (isMobileLandscape.value) {
      sidebar.isSidebarCollapsed.value = true;
    }
  });

  // The tabs drawer only exists on phones. Still "open" once the window is
  // wider than that, it would hold the desktop sidebar expanded and stop the
  // collapse button working, so close it on the way out of mobile.
  effect(() => {
    if (!isMobile.value) {
      sidebar.isMobileOpen.value = false;
    }
  });

  // The "compact desktop" band (just above the mobile breakpoint): an
  // expanded sidebar would overlay the reader rather than dock beside it
  // (see components/Tabs/Tabs.css), so start collapsed to a rail when entering the band.
  // Same once-per-transition pattern as the landscape collapse above, so the
  // user can still expand the floating sidebar afterwards.
  const isCompactDesktop = computed(
    () =>
      viewportWidth.value > MOBILE_BREAKPOINT &&
      viewportWidth.value <= SIDEBAR_OVERLAY_MAX_WIDTH
  );
  effect(() => {
    if (isCompactDesktop.value) {
      sidebar.isSidebarCollapsed.value = true;
    }
  });

  // New visitors on desktop — signed in or not — start with the rail
  // collapsed, so the welcome screen isn't competing with an open sidebar.
  // A saved choice wins, and it is applied after mount (via
  // `hydrateFromStorage`) so the first render still matches the expanded rail
  // the server painted. The local tour flags are enough to decide; waiting
  // on the account profile left signed-in visitors on the open rail until
  // that request returned.
  //
  // Left unarmed until then on purpose: reading `localStorage` at construction
  // would collapse the client tree and not the SSR HTML. The default itself
  // is not stored — only a toggle is — and a phone visit doesn't mark the
  // decision done, so resizing up to desktop still gets the new-user collapse.
  let sidebarCollapsedArmed = false;
  let sidebarCollapsedHydrated = false;
  const armSidebarCollapsed = () => {
    if (sidebarCollapsedArmed) {
      return;
    }
    sidebarCollapsedArmed = true;
    effect(() => {
      if (sidebarCollapsedHydrated) {
        return;
      }
      if (typeof window === "undefined") {
        sidebarCollapsedHydrated = true;
        return;
      }

      const storedApplied = sidebar.hydrateStoredCollapsed();
      const mobile = isMobile.value;

      if (
        !storedApplied &&
        !mobile &&
        !openedViaContentLink.peek() &&
        !tutorial.completed.value &&
        !tutorial.optedOut.value
      ) {
        // The signal only. Writing it would turn the default into a saved
        // choice, so a later change to that default would never reach a
        // visitor who never toggled the rail.
        sidebar.isSidebarCollapsed.value = true;
      }

      // Applying a saved "expanded" choice undoes the band collapse the
      // effects above already did at startup. Put it back without writing
      // storage — the band is a viewport constraint, not a preference.
      if (isCompactDesktop.value || isMobileLandscape.value) {
        sidebar.isSidebarCollapsed.value = true;
      }

      if (!storedApplied && mobile) {
        return;
      }

      sidebarCollapsedHydrated = true;
    });
  };

  const effectiveSlots = computed(() => {
    if (!panelsEnabled.value) {
      // tabsLayout.setLayout already forces "single" whenever panelsEnabled
      // is false, but the manager's stored slots may still lag behind a
      // config change that happened without an explicit setLayout call, so
      // clamp here too. This is applied to the rendered view only — the
      // manager's own slots are left untouched so they are restored when
      // panels are re-enabled.
      const allSlots = tabsLayout.slots.value;
      const tab = selectedTab.value;
      const preferred =
        (tab ? allSlots.find((s) => s.tab?.id === tab.id) : null) ??
        allSlots[0] ??
        null;
      return preferred ? [preferred] : [];
    }
    if (isMobile.value) {
      // Mobile shows exactly one slot at a time — never stacked — so a single
      // reader fills the small viewport. This is applied to the rendered view
      // only: the tabs layout manager's own settings (layout, extra slots) are
      // left untouched so they are restored when the viewport grows back to a
      // desktop size. We keep the slot hosting the currently selected tab
      // (falling back to the manager's selected slot, then the first slot).
      const allSlots = tabsLayout.slots.value;
      if (allSlots.length === 0) {
        return [];
      }

      const tab = selectedTab.value;
      const selectedSlotId = tabsLayout.selectedSlotId.value;
      const preferred =
        (tab ? allSlots.find((s) => s.tab?.id === tab.id) : null) ??
        allSlots.find((s) => s.id === selectedSlotId) ??
        allSlots[0]!;
      return [preferred];
    }
    return tabsLayout.slots.value;
  });

  const effectiveSlotLayout = computed<TabSlotLayoutId>(() => {
    if (!panelsEnabled.value) {
      return "single";
    }
    if (isMobile.value) {
      // Mobile never stacks — a single slot fills the viewport (see
      // effectiveSlots).
      return "single";
    }
    return tabsLayout.layout.value;
  });

  // Effective custom-pane list shown by the UI: on mobile every pane is
  // forced to fullscreen placement for rendering only — the manager's stored
  // placement is left untouched so it's restored on a desktop-size viewport.
  const effectivePanes = computed(() => {
    if (!isMobile.value) {
      return panes.panes.value;
    }
    return panes.panes.value.map((pane) =>
      pane.placement === "fullscreen"
        ? pane
        : { ...pane, placement: "fullscreen" as const }
    );
  });
  const currentReadingState = computed(() => {
    const selectedTabValue = selectedTab.value;

    if (!selectedTabValue) {
      return null;
    }

    return {
      tab: selectedTabValue,

      translationId: selectedTabValue.readingState.translationId.value,
      bookId: selectedTabValue.readingState.bookId.value,
      chapterNumber: selectedTabValue.readingState.chapterNumber.value,
    };
  });

  // Keep selection and slots aligned. Clicking a tab goes through
  // handleSelectTab (which also calls setSelectedSlotTab). removeTab only
  // updates selectedTabId — TabsLayoutManager's sync then clears the closed
  // tab from its slot (tab → null). If that emptied slot is still selected,
  // put the newly selected tab into it so TabsLayout keeps rendering a reader.
  //
  // Only fill empty slots: never overwrite a slot that already has a tab.
  // openInNewSlot clones via addTab (which selects the clone) before the new
  // slot exists; overwriting here would steal the original slot and leave an
  // empty pane after layout de-dupes the shared tab id.
  effect(() => {
    const tab = selectedTab.value;
    if (!tab) {
      return;
    }

    const matchingSlot =
      tabsLayout.slots.value.find((s) => s.tab?.id === tab.id) ?? null;
    if (matchingSlot) {
      tabsLayout.selectSlot(matchingSlot.id);
      return;
    }

    const selectedSlot =
      tabsLayout.slots.value.find(
        (s) => s.id === tabsLayout.selectedSlotId.value
      ) ??
      tabsLayout.slots.value[0] ??
      null;
    if (selectedSlot && !selectedSlot.tab) {
      tabsLayout.setSelectedSlotTab(tab.id);
    }
  });

  // Blocks the persistence effect below until `hydrateFromStorage` has read the
  // stored tabs back. Until then the managers hold only the single URL-derived
  // tab they seed with to match SSR, and persisting *that* would overwrite the
  // visitor's saved tabs with a one-tab state before anything ever restored
  // them — the saved session would be destroyed by the act of loading the page.
  const tabsRestored = signal(false);

  // Persist the non-ephemeral tab state (translation/book/chapter per tab, the
  // selected tab, the layout preset, and the slot arrangement) to localStorage
  // so TabsManager/TabsLayoutManager can restore it on the next refresh or
  // revisit. Session-backed tabs are skipped — they rejoin via `?sessionId=`
  // (see setupInitialSession). Client-only: there is no localStorage in SSR.
  if (typeof window !== "undefined") {
    let lastSerialized: string | null = null;

    const buildPersistedTabs = (): PersistedTab[] =>
      tabs.tabs.value
        .filter((tab) => !tab.sharedSession)
        .map((tab) => {
          const persisted: PersistedTab = {
            id: tab.id,
            translationId: tab.readingState.translationId.value,
            bookId: tab.readingState.bookId.value,
            chapterNumber: tab.readingState.chapterNumber.value,
          };
          if (tab.slotOnly) {
            persisted.slotOnly = true;
          }
          return persisted;
        });

    effect(() => {
      if (!tabsRestored.value) {
        return;
      }
      // The share-sheet preview is this same origin, so it shares the
      // reader's localStorage. Writing here would replace their tabs with
      // whatever chapter the preview was left on.
      if (isMinimalEmbed.value) {
        return;
      }

      const persistedTabs = buildPersistedTabs();
      const persistableIds = new Set(persistedTabs.map((tab) => tab.id));

      const currentSlots = tabsLayout.slots.value;
      const slotTabIds = currentSlots.map((slot) =>
        slot.tab && persistableIds.has(slot.tab.id) ? slot.tab.id : null
      );
      const selectedSlotIndex = currentSlots.findIndex(
        (slot) => slot.id === tabsLayout.selectedSlotId.value
      );

      const currentSelectedTabId = tabs.selectedTabId.value;
      const selectedTabId = persistableIds.has(currentSelectedTabId)
        ? currentSelectedTabId
        : (persistedTabs[0]?.id ?? "");

      const nextState = {
        tabs: persistedTabs,
        selectedTabId,
        layout: tabsLayout.layout.value,
        slotTabIds,
        selectedSlotIndex: selectedSlotIndex >= 0 ? selectedSlotIndex : null,
      };

      // Position signals change often during navigation; skip writes that would
      // not change what is stored.
      const serialized = JSON.stringify(nextState);
      if (serialized === lastSerialized) {
        return;
      }
      lastSerialized = serialized;
      writeStoredTabsState(nextState);
    });
  }

  /**
   * Whether the current URL is a static, non-reading page (currently just
   * "/{lang}/about"). Drives the About fullscreen-pane's open/close effect
   * below and the meta-signal branches further down — a single source of
   * truth so the rendered content and its title/description/canonical
   * always agree.
   */
  const isAboutPage = computed(
    () =>
      parseStaticPagePath(
        navigation.currentUrl.value.pathname,
        navigation.basePath
      )?.page === "about"
  );

  /**
   * One-time correction of every `localStorage`-derived value that feeds the
   * first render. See `AppState.hydrateFromStorage`.
   */
  const hydrateFromStorage = () => {
    // `batch` flushes its effects before it returns, so the reader's write of
    // the restored position to the URL lands inside this window.
    restoringStoredState = true;
    try {
      batch(() => {
        // Tabs before slots: slots are bound to tab objects by id, and restoring
        // tabs replaces those objects wholesale.
        tabs.hydrateStoredTabs();
        tabsLayout.hydrateStoredLayout();
        data.hydrateCachedCatalog();
        selector.hydrateStoredViewMode();
        tutorial.hydrateStoredFlags();
        onboarding.hydrateStoredFlags();
        // Unblocks the persistence effect above, which now sees the restored tab
        // list rather than the URL-only seed.
        tabsRestored.value = true;
      });
    } finally {
      restoringStoredState = false;
    }
    // Restoring can replace the tab a playing playlist's URL started playback
    // on; start it again on whichever tab is active now.
    playlists.resumePlaybackFromUrl();
    // Deliberately outside the batch: this can set `promptVisible`, and it must
    // observe the settled reader state rather than a half-applied one.
    tutorial.armAutoStart();
    // After the tutorial flags, so "new user" sees the stored seen/opted-out
    // state rather than the empty SSR seed.
    armSidebarCollapsed();
  };

  /**
   * The step a playlist is playing at when the URL is its playing path
   * (1-based); null on a shared page itself, or anywhere else.
   */
  const sharedPageStep = computed<number | null>(
    () =>
      parseSharedPagePath(
        navigation.currentUrl.value.pathname,
        navigation.basePath
      )?.step ?? null
  );

  /** The shared playlist or reading plan page the URL is on, once it has loaded. */
  const sharedPage = computed<{
    kind: SharedPageKind;
    locator: string;
    title: string | null;
    description: string | null;
    heroImageUrl: string | null | undefined;
    authorName: string | null;
  } | null>(() => {
    const playlistPage = playlists.playlistPage.value;
    if (playlistPage) {
      return {
        kind: "playlist",
        locator: playlistPage.locator,
        title: playlistPage.item.title,
        description: playlistPage.item.description,
        heroImageUrl: playlistPage.item.heroImageUrl,
        authorName: playlistPage.authorName,
      };
    }
    const planPage = readingPlans.readingPlanPage.value;
    if (planPage) {
      return {
        kind: "readingPlan",
        locator: planPage.locator,
        title: planPage.item.title,
        description: planPage.item.description,
        heroImageUrl: planPage.item.heroImageUrl,
        authorName: planPage.authorName,
      };
    }
    return null;
  });

  /** The kind of shared page the URL is on when its record was looked up and doesn't exist. */
  const sharedPageNotFoundKind = computed<SharedPageKind | null>(() =>
    playlists.playlistPageNotFound.value
      ? "playlist"
      : readingPlans.readingPlanPageNotFound.value
        ? "readingPlan"
        : null
  );

  /** "Playlist not found" / "Reading plan not found", or null. */
  const sharedPageNotFoundTitle = computed<string | null>(() => {
    const kind = sharedPageNotFoundKind.value;
    if (!kind) {
      return null;
    }
    void i18n.language.value;
    const { t } = i18n;
    return kind === "playlist"
      ? t("playlist-not-found-title", { defaultValue: "Playlist not found" })
      : t("reading-plan-not-found-title", {
          defaultValue: "Reading plan not found",
        });
  });

  /** The kind of shared page the URL is on when its record failed to load (not "not found"). */
  const sharedPageLoadFailedKind = computed<SharedPageKind | null>(() =>
    playlists.playlistPageLoadFailed.value
      ? "playlist"
      : readingPlans.readingPlanPageLoadFailed.value
        ? "readingPlan"
        : null
  );

  /** "Couldn't load playlist" / "Couldn't load reading plan", or null. */
  const sharedPageLoadFailedTitle = computed<string | null>(() => {
    const kind = sharedPageLoadFailedKind.value;
    if (!kind) {
      return null;
    }
    void i18n.language.value;
    const { t } = i18n;
    return kind === "playlist"
      ? t("playlist-load-failed-title", {
          defaultValue: "Couldn't load playlist",
        })
      : t("reading-plan-load-failed-title", {
          defaultValue: "Couldn't load reading plan",
        });
  });

  /** The shared page's title, falling back to "Untitled …" for its kind. */
  const sharedPageName = computed<string | null>(() => {
    const page = sharedPage.value;
    if (!page) {
      return null;
    }
    void i18n.language.value;
    const { t } = i18n;
    return (
      page.title ||
      (page.kind === "playlist"
        ? t("untitled-playlist", { defaultValue: "Untitled playlist" })
        : t("untitled-reading-plan", { defaultValue: "Untitled plan" }))
    );
  });

  /** "{title} by {author}" while on a shared page, else null. */
  const sharedPageTitle = computed<string | null>(() => {
    const page = sharedPage.value;
    const name = sharedPageName.value;
    if (!page || !name) {
      return null;
    }
    const { t } = i18n;
    return page.authorName
      ? t("shared-page-title", {
          title: name,
          author: page.authorName,
          defaultValue: "{{title}} by {{author}}",
        })
      : name;
  });

  const title = computed(() => {
    const RTLE_CHAR = "\u202B";
    void i18n.language.value;
    const isRtl = i18n.isRtl.value;

    const { t } = i18n;

    const seedBibleTitle = getBrandedAppText(
      t("seed-bible", { defaultValue: "Seed Bible" }),
      t,
      branding,
      customizations.activeCustomization.value?.name
    );

    const getTitle = () => {
      if (isAboutPage.value) {
        return `${t("about-title", { defaultValue: "About Seed Bible" })} | ${seedBibleTitle}`;
      }

      // Only on the shared page itself: while a playlist plays at its own
      // path, the tab is titled after the chapter like any other reading.
      if (sharedPageTitle.value && sharedPageStep.value == null) {
        return `${sharedPageTitle.value} | ${seedBibleTitle}`;
      }

      if (sharedPageNotFoundTitle.value) {
        return `${sharedPageNotFoundTitle.value} | ${seedBibleTitle}`;
      }

      if (sharedPageLoadFailedTitle.value) {
        return `${sharedPageLoadFailedTitle.value} | ${seedBibleTitle}`;
      }

      if (!selectedTab.value) {
        return seedBibleTitle;
      }

      return `${selectedTab.value.readingState.title.value} - ${selectedTab.value.readingState.subTitle.value} | ${seedBibleTitle}`;
    };

    return `${isRtl ? RTLE_CHAR : ""}${getTitle()}`;
  });

  const description = computed(() => {
    void i18n.language.value;
    const { t } = i18n;

    if (isAboutPage.value) {
      return truncateForMeta(
        t("about-meta-description", {
          defaultValue:
            "Seed Bible is a free Bible app with dozens of translations, reading plans, notes, highlights, and study tools.",
        }),
        META_DESCRIPTION_MAX_GRAPHEMES
      );
    }

    const page = sharedPage.value;
    if (page) {
      return truncateForMeta(
        page.description?.trim() ||
          (page.kind === "playlist"
            ? t("playlist-page-meta-description", {
                defaultValue: "A Bible reading playlist on Seed Bible.",
              })
            : t("reading-plan-page-meta-description", {
                defaultValue: "A Bible reading plan on Seed Bible.",
              })),
        META_DESCRIPTION_MAX_GRAPHEMES
      );
    }

    const chapter = selectedTab.value?.readingState.chapterData.value;
    if (!chapter) {
      // Truncated like every other branch: this key is translatable, and a
      // translation is free to be longer than the English default.
      return truncateForMeta(
        t("app-meta-description", { defaultValue: APP_META_DESCRIPTION }),
        META_DESCRIPTION_MAX_GRAPHEMES
      );
    }

    // Used whenever there is no scripture to quote — an empty chapter payload,
    // or a reference so long it leaves no room for any.
    const referenceOnly = () =>
      truncateForMeta(
        t("seed-bible-description", {
          bookName: chapter.book.name,
          chapterNumber: chapter.chapter.number,
          defaultValue: "Read {{bookName}} {{chapterNumber}} in the Seed Bible",
        }),
        META_DESCRIPTION_MAX_GRAPHEMES
      );

    const excerpt = buildChapterExcerpt(
      chapter.chapter.content,
      META_DESCRIPTION_MAX_GRAPHEMES
    );
    if (!excerpt) {
      return referenceOnly();
    }

    const compose = (scripture: string) =>
      t("chapter-meta-description", {
        bookName: chapter.book.name,
        chapterNumber: chapter.chapter.number,
        translationName: chapter.translation.shortName,
        excerpt: scripture,
        defaultValue:
          "{{bookName}} {{chapterNumber}} ({{translationName}}): {{excerpt}}",
      });

    // Charge the citation against the budget first, so what gets cut is always
    // scripture. Truncating only the composed string would instead chop the
    // citation off the end for any locale whose template puts it last.
    const scriptureBudget =
      META_DESCRIPTION_MAX_GRAPHEMES - countGraphemes(compose(""));
    const fitted =
      scriptureBudget > 0 ? truncateForMeta(excerpt, scriptureBudget) : "";
    if (!fitted) {
      return referenceOnly();
    }

    // Backstop for a template whose own literal text overruns the budget.
    return truncateForMeta(compose(fitted), META_DESCRIPTION_MAX_GRAPHEMES);
  });

  const siteName = computed(() => {
    void i18n.language.value;
    const { t } = i18n;

    return getBrandedAppText(
      t("seed-bible", { defaultValue: "Seed Bible" }),
      t,
      branding,
      customizations.activeCustomization.value?.name
    );
  });

  const customizationLogoUrl = computed<string | null>(
    () => customizations.activeCustomization.value?.logoUrl ?? null
  );

  const socialImage = computed<{ url: string; alt: string } | null>(() => {
    const heroImageUrl = sharedPage.value?.heroImageUrl;
    if (heroImageUrl) {
      return { url: heroImageUrl, alt: sharedPageTitle.value ?? "" };
    }
    const logoUrl = customizationLogoUrl.value;
    return logoUrl ? { url: logoUrl, alt: siteName.value } : null;
  });

  /**
   * Read only when rendering meta tags on the server (see `entry-ssr.tsx`),
   * along with `description`.
   *
   * These two stay derived from the *loaded chapter*, unlike the reader's own
   * titles, which are derived from the book catalog so they can move the instant
   * navigation happens. Two reasons: a server render has no navigation to lag
   * behind, and it suspends until the first chapter settles, so content is there
   * whenever it could be. And when it genuinely isn't — a failed load — falling
   * back to a generic blurb is better than describing a chapter the server
   * could not actually serve.
   *
   * `title` is the exception and is position-derived: it also drives
   * `document.title` on the client, where the lag would be visible.
   * `canonicalUrl` is position-derived too, for a different reason — see there.
   */
  const socialTitle = computed(() => {
    void i18n.language.value;
    const { t } = i18n;

    if (isAboutPage.value) {
      return t("about-title", { defaultValue: "About Seed Bible" });
    }

    if (sharedPageTitle.value) {
      return sharedPageTitle.value;
    }

    const chapter = selectedTab.value?.readingState.chapterData.value;
    if (!chapter) {
      return t("read-the-bible", {
        defaultValue: "Read the Bible",
      });
    }

    return t("social-title", {
      defaultValue: "Read {{bookName}} {{chapterNumber}}",
      bookName: chapter.book.name,
      chapterNumber: chapter.chapter.number,
    });
  });

  /**
   * The one URL that should be indexed for whatever the reader is looking at.
   *
   * Derived from the reading *position* signals, not from `chapterData`. Those
   * are set from the URL when the tab is constructed, with no network involved,
   * so this stays correct in the three cases where a chapter never arrives — an
   * API failure, a book absent from the translation, or the server's five-second
   * load timeout. Keying off the loaded chapter meant all three server-rendered
   * as `<link rel="canonical" href="/">`, pointing the whole site at its own
   * front page. A transient upstream failure shouldn't change a chapter's
   * address, and a book that doesn't exist at all is already answered with a 404
   * (see `entry-ssr.tsx`), so there is nothing left for the old fallback to
   * protect against.
   *
   * The language segment follows the *translation*, not the reader's UI
   * language: someone reading the English AAB with a French interface is
   * looking at the same scripture as someone reading it in English, so both
   * point at `/en/AAB/…` instead of each claiming to be canonical. This is the
   * same mapping the sitemap generator uses, so the URLs it publishes and the
   * pages they lead to agree.
   *
   * Always the explicit four-segment form. The short three-segment form is a
   * redirect entry point — requested without an `Accept-Language` header, which
   * is what a crawler sends, it 302s to the explicit form — so naming it here
   * would point every canonical at a URL that redirects.
   */
  const canonicalUrl = computed(() => {
    if (isAboutPage.value) {
      // Uses the *resolved* i18n language, not the raw URL segment, so a
      // garbage/unsupported language in the URL still canonicalizes to a
      // real page instead of echoing back something that doesn't exist.
      return `${navigation.basePath}${buildStaticPagePath({
        language: i18n.language.value,
        page: "about",
      })}`;
    }

    const page = sharedPage.value;
    if (page) {
      return `${navigation.basePath}${buildSharedPagePath({
        kind: page.kind,
        language: i18n.language.value,
        locator: page.locator,
        title: page.title,
      })}`;
    }

    const readingState = selectedTab.value?.readingState;
    const bookId = readingState?.bookId.value;

    if (!readingState || !bookId) {
      return navigation.basePath || "/";
    }

    const translationId = data.buildTranslationId(
      readingState.translationId.value
    );
    // Falls back to the current UI language, unlike the chapter tool links'
    // version of this chain (see `resolveTranslationUiLanguage`) — this is
    // the page actually being rendered right now, so it has a real "current
    // visitor" to derive one from.
    const language = resolveTranslationUiLanguage({
      translationLanguage: readingState.translation.value?.language,
      translationId,
      fallback: i18n.language.value,
    });

    const readingPath = buildReadingPath({
      language,
      translationId,
      bookId: bookId as BookId,
      chapter: readingState.chapterNumber.value,
    });

    return `${navigation.basePath}${readingPath}`;
  });

  /** How often time spent on the chapter in view is written to history. */
  const READING_TICK_MS = 5000;
  /**
   * The most one tick may credit. A page can be frozen without ever reporting
   * itself hidden, and its next tick then arrives with the whole sleep behind
   * it; this caps what that tick can claim to have watched.
   */
  const READING_MAX_TICK_CREDIT_MS = READING_TICK_MS * 2;
  const disposeReadingTicker = effect(() => {
    // Same origin as the page around the share preview, so time the preview
    // sits open must not become the viewer's reading history.
    if (isMinimalEmbed.value) {
      return;
    }
    if (!selectedTab.value) {
      return;
    }

    const chapter = selectedTab.value.readingState.chapterData.value;
    if (!chapter) {
      return;
    }

    // Reading history records book and chapter but not which translation they
    // were read in, so the offline manager keeps its own first-seen stamp —
    // that's what the "used for a day" download offer is judged against.
    data.offline.noteTranslationInUse(chapter.translation.id);

    // Nobody is reading a server render, and an interval started here would
    // keep the whole per-request state alive on the SSR host.
    if (import.meta.env.SSR) {
      return;
    }

    // Reading time accrues only while the chapter is actually on screen. Each
    // tick credits just the stretch since the last one rather than stretching
    // the event's end to the present, so time the app slept through — a locked
    // phone, a backgrounded tab — opens a fresh event on return instead of
    // being back-filled as though it had been read. Time spent listening with
    // the screen off is recorded separately, by whatever is playing the audio.
    let creditedThroughMs = Date.now();
    let readingTicker: ReturnType<typeof setInterval> | null = null;

    const creditTimeOnScreen = () => {
      const now = Date.now();
      const from = Math.max(
        creditedThroughMs,
        now - READING_MAX_TICK_CREDIT_MS
      );
      creditedThroughMs = now;
      readingHistory.saveReadingSpan(
        chapter.book.id,
        chapter.chapter.number,
        Math.floor(from / 1000),
        Math.floor(now / 1000)
      );
    };

    const startCrediting = () => {
      if (readingTicker !== null) {
        return;
      }
      creditedThroughMs = Date.now();
      readingTicker = setInterval(creditTimeOnScreen, READING_TICK_MS);
    };

    const stopCrediting = () => {
      if (readingTicker === null) {
        return;
      }
      clearInterval(readingTicker);
      readingTicker = null;
      // The part-tick since the last one goes uncredited on purpose: a chapter
      // has always had to hold the screen for a whole tick before it counts as
      // read at all, and crediting stragglers here would let one flicked past
      // on the way somewhere else earn a place in history.
    };

    const canWatchVisibility = typeof document !== "undefined";
    // A visible tab isn't enough: Today (or any fullscreen pane) can cover the
    // reader, and a chapter sitting under Welcome would otherwise be credited
    // every tick — giving a brand-new account history it never read.
    const syncCrediting = () => {
      const tabVisible =
        !canWatchVisibility || document.visibilityState === "visible";
      if (tabVisible && readerVisible.peek()) {
        startCrediting();
      } else {
        stopCrediting();
      }
    };

    if (canWatchVisibility) {
      document.addEventListener("visibilitychange", syncCrediting);
    }
    // Subscribed rather than read in this effect, so a pane opening or closing
    // doesn't re-run it and restart the `user_chapter_read` timer below.
    const stopWatchingReader = readerVisible.subscribe(syncCrediting);

    const posthogTimeoutId = setTimeout(() => {
      captureEvent("user_chapter_read", {
        translationId: chapter.translation.id,
        bookId: chapter.book.id,
        chapter: String(chapter.chapter.number),
      });
    }, 30_000);

    return () => {
      if (canWatchVisibility) {
        document.removeEventListener("visibilitychange", syncCrediting);
      }
      stopWatchingReader();
      stopCrediting();
      clearTimeout(posthogTimeoutId);
    };
  });

  // Offer to save the current translation for offline reading, once the
  // tutorial and install prompts have had their turn so we never stack two
  // dialogs. One-shot per load via `downloadOfferChecked`; the manager decides
  // whether the offer is actually warranted (first save on a device with
  // nothing downloaded, or the current translation after a day). Leaving the
  // introduction early never resolves the install offer, so this effect
  // simply doesn't run that visit — it has no separate "come back next time"
  // flag. The next visit follows these same rules.
  let downloadOfferChecked = false;
  effect(() => {
    if (downloadOfferChecked) {
      return;
    }
    if (isMinimalEmbed.value) {
      downloadOfferChecked = true;
      return;
    }
    if (openedViaContentLink.value) {
      return;
    }
    if (!installOfferResolved.value) {
      return;
    }
    // The install modal is up until the user resolves it.
    if (onboarding.step.value !== "done") {
      return;
    }
    if (tutorial.promptVisible.value || tutorial.running.value) {
      return;
    }
    const chapter = selectedTab.value?.readingState.chapterData.value;
    if (!chapter) {
      return;
    }
    downloadOfferChecked = true;
    data.offline.offerDownloadPrompt(chapter.translation);
  });

  const closeSidebarAndSettings = () => {
    sidebar.closeSettings();
    sidebar.closeSidebar();
  };

  /**
   * Dismisses the sidebar and runs a navigation as a single history entry.
   * Both halves write the URL — the sidebar drops `?sidebar=open`, the reader
   * writes the new position — and each write on its own would cost a history
   * entry, so pressing back afterwards would land on a stale duplicate of the
   * destination instead of where the user actually came from.
   */
  const navigateFromSidebar = (navigate: () => void) => {
    navigation.batchWrites(() => {
      closeSidebarAndSettings();
      navigate();
    });
  };

  const handleSelectTab = (tabId: string) => {
    navigateFromSidebar(() => {
      tabs.selectTab(tabId);
      tabsLayout.setSelectedSlotTab(tabId);
      panes.closeFullscreenPanes();
    });
  };

  const handleAddTab = () => {
    closeSidebarAndSettings();
    const targetSlot =
      tabsLayout.slots.value.find(
        (slot) => slot.id === tabsLayout.selectedSlotId.value
      ) ?? tabsLayout.slots.value[0]!;

    void selector.setOpen(true, targetSlot, { forNewTab: true });
  };

  const handleOpenInNewSlot = (tabId: string) => {
    navigateFromSidebar(() => {
      const slot = tabsLayout.openTabInNewSlot(tabId);
      if (slot?.tab) {
        tabs.selectTab(slot.tab.id);
      }
    });
  };

  const handleSelectSlot = (slotId: string) => {
    navigateFromSidebar(() => {
      tabsLayout.selectSlot(slotId);

      const selectedSlot =
        tabsLayout.slots.value.find((slot) => slot.id === slotId) ?? null;
      if (selectedSlot?.tab) {
        tabs.selectTab(selectedSlot.tab.id);
      }
    });
  };

  const handleSelectPane = (paneId: string) => {
    // Side and fullscreen panes render docked beside the sidebar on desktop
    // (see PaneLayout's SidePane/FullscreenPane) rather than covering it, so
    // interacting with one shouldn't close it there — the Customization
    // Center's editor pane in particular depends on the Settings list
    // staying open behind it. On mobile every pane is forced to fullscreen
    // (see `effectivePanes`) and the sidebar is a full-screen drawer, so it
    // still needs to close to reveal the pane — batched with the pane
    // selection via `navigateFromSidebar` so the sidebar/settings URL writes
    // don't cost the back button a stale extra history entry.
    if (isMobile.value) {
      navigateFromSidebar(() => {
        panes.selectPane(paneId);
      });
    } else {
      panes.selectPane(paneId);
    }
  };

  // App-level toast queue: popups shown one at a time at the bottom of the
  // screen, the head of the queue being the one on screen (see `toast` on
  // `AppState` for the timing and `key` rules). The incrementing id keys the
  // render so the slide-in animation replays even for a repeated message.
  //
  // Defined here (rather than further down, where it's exposed on `state`)
  // because the host-disconnect handling below also calls it, and that
  // effect runs immediately when constructed.
  let toastSeq = 0;
  const toastQueue = signal<AppToast[]>([]);
  const dismissToast = (id: number) => {
    toastQueue.value = toastQueue.peek().filter((queued) => queued.id !== id);
  };
  const toast = (message: string, options?: ToastOptions) => {
    const id = ++toastSeq;
    const onClick = options?.onClick;
    const entry: AppToast = {
      id,
      message,
      key: options?.key,
      hint: options?.hint,
      onClick: onClick
        ? () => {
            dismissToast(id);
            onClick();
          }
        : undefined,
    };
    // `peek()`: toast() is called from inside effects, and a tracked read
    // would subscribe that effect to the queue it is writing.
    const queue = toastQueue.peek();
    const key = entry.key;
    if (key === undefined) {
      toastQueue.value = [...queue, entry];
      return;
    }
    const [head, ...waiting] = queue;
    const alreadyOnScreen = head?.key === key && head.message === message;
    toastQueue.value = [
      ...(head ? [head] : []),
      ...waiting.filter((queued) => queued.key !== key),
      ...(alreadyOnScreen ? [] : [entry]),
    ];
  };

  const MIN_TOAST_MS = 1500;
  const FULL_TOAST_MS = 3500;
  const TAPPABLE_TOAST_MS = 6000;
  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  let shownId: number | null = null;
  let shownAt = 0;
  const currentToast = computed(() => toastQueue.value[0] ?? null);

  toastQueue.subscribe((queue) => {
    if (toastTimer !== null) clearTimeout(toastTimer);
    toastTimer = null;

    const head = queue[0];
    if (!head) {
      shownId = null;
      return;
    }
    if (head.id !== shownId) {
      shownId = head.id;
      shownAt = Date.now();
    }

    const duration = head.onClick
      ? TAPPABLE_TOAST_MS
      : queue.length > 1
        ? MIN_TOAST_MS
        : FULL_TOAST_MS;
    const remaining = Math.max(0, duration - (Date.now() - shownAt));
    toastTimer = setTimeout(() => {
      toastQueue.value = toastQueue.value.slice(1);
    }, remaining);
  });

  // Wraps a session so that when it's disposed (via tabs.removeTab), its
  // entry is removed from the global shared-sessions registry too. The
  // registry is opened by every client, so other users see the session
  // disappear automatically.
  //
  // Additionally: if THIS client is the host (by user id or connection id),
  // mark `endedAt` in the session's shared options before tearing down.
  // Participants watch for `endedAt` and auto-close their tab (see the
  // effect below), which is the behavior the host expects when they end
  // or close a shared tab.
  const wrapSessionLifecycle = (
    session: BibleReadingSession
  ): BibleReadingSession => {
    const originalDispose = session.dispose.bind(session);
    session.dispose = () => {
      const hostUserId = session.options.value.hostUserId;
      const localId = login.userId.value;
      const localConnectionId = os.connectionId;
      // Trust `locallyHostedSessionIds` over the identity comparison —
      // after a login/logout the current login.userId / configBot.id no
      // longer match the original `hostUserId`, but we're still the host
      // of sessions we created in this run.
      const options = session.options.value;
      const isHost =
        locallyHostedSessionIds.has(session.id) ||
        (hostUserId !== null &&
          (hostUserId === localId || hostUserId === localConnectionId)) ||
        isSessionHost(options, localId) ||
        isSessionHost(options, localConnectionId);

      // Only end the session for everyone when the leaving client is the
      // LAST connected host/co-host. If another host/co-host is still
      // present (e.g. one was just appointed), hand the session off instead
      // of ending it. Explicit "End session" writes `endedAt` directly, so
      // it bypasses this heuristic.
      const anotherHostStillConnected = session.connectedUsers.value.some(
        (user) =>
          !user.isSelf &&
          (isSessionHost(options, user.userId) ||
            isSessionHost(options, user.connectionId))
      );

      if (isHost && !anotherHostStillConnected && options.endedAt === null) {
        try {
          session.updateOptions({ endedAt: Date.now() });
        } catch {
          // Best-effort — don't block teardown if the CRDT write fails.
        }
      }

      locallyHostedSessionIds.delete(session.id);
      void invitations.unpublishSession(session.id);
      originalDispose();
    };
    return session;
  };

  // Sessions THIS client created locally. The host-disconnect auto-close
  // below skips these — when the local user logs in/out the OS may
  // re-establish the underlying connection with a new connectionId/userId,
  // which would make the original `hostUserId` look "disconnected" even
  // though the host (us) is still right here. The host's own tab only
  // closes through explicit teardown (close button / "End Session"),
  // never through the disconnect heuristic.
  const locallyHostedSessionIds = new Set<string>();

  // Suppresses the host-disconnect grace timer for a short window right
  // after the tab returns to the foreground. On mobile, backgrounding the
  // app lets its own connection go stale; right as it resumes, this
  // client's own `connectedUsers` view can still read "host missing" for a
  // few seconds even though the host never left. `session.isSynced` (below)
  // covers most of this, but the underlying connection can briefly report
  // itself synced again a beat before it's actually caught up, so this is
  // extra insurance layered on top of it.
  const RESUME_GRACE_MS = 5000;
  const justResumedFromBackground = signal(false);
  let resumeGraceTimer: ReturnType<typeof setTimeout> | null = null;
  if (typeof document !== "undefined" && !import.meta.env.SSR) {
    effect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") return;
        justResumedFromBackground.value = true;
        if (resumeGraceTimer !== null) {
          clearTimeout(resumeGraceTimer);
        }
        resumeGraceTimer = setTimeout(() => {
          justResumedFromBackground.value = false;
          resumeGraceTimer = null;
        }, RESUME_GRACE_MS);
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () =>
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
    });
  }

  // Auto-close participant tabs when the host goes away. Two signals:
  //  (a) `options.endedAt` was written by the host (clean "End Session"
  //      action — works when the CRDT flushes before the host disconnects).
  //  (b) The host was previously in `connectedUsers` but has since left —
  //      catches the host-browser-close case even if the `endedAt` write
  //      never made it across the wire.
  //
  // We remember per-session that the host was at least once connected, so
  // joiners don't immediately close their own tab on connect before they
  // even see the host. We also debounce the disconnect-close path — a
  // host who logs in mid-session will briefly look disconnected (their
  // OS connection re-establishes with a new identity), and we want their
  // updated `hostUserId` to land via the CRDT before we close the tab.
  //
  // Signal (b) is judged from THIS client's own `connectedUsers` view,
  // which is only trustworthy while this client's own connection is
  // synced (`session.isSynced`) and not still catching up right after a
  // mobile resume (`justResumedFromBackground`). Otherwise a client's own
  // stale/resyncing connection can make the host look gone when it never
  // left — see issue #1346.
  const sessionsWhereHostWasSeen = new Set<string>();
  const sessionsWhereWeLostConnection = new Set<string>();
  // Sessions where the "you lost connection" toast was actually shown (it is
  // suppressed right after a resume). Only those owe a "you rejoined" toast.
  const sessionsWhereWeAnnouncedDrop = new Set<string>();
  // After we recover from our own drop, presence can still omit the host
  // for a beat (the peer list is rebuilt on resync). Don't treat that as
  // "the host left and came back" — prefer the you-rejoined toast.
  const SELF_RECONNECT_SETTLE_MS = 2000;
  const sessionsSettlingAfterSelfReconnect = new Set<string>();
  const presenceSettleTick = signal(0);
  const pendingPresenceSettleTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  const HOST_DISCONNECT_GRACE_MS = 30000;
  const pendingHostDisconnectTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  const clearPendingPresenceSettle = (sessionId: string): void => {
    sessionsSettlingAfterSelfReconnect.delete(sessionId);
    const timer = pendingPresenceSettleTimers.get(sessionId);
    if (timer !== undefined) {
      clearTimeout(timer);
      pendingPresenceSettleTimers.delete(sessionId);
    }
  };
  const forgetSessionPresence = (sessionId: string): void => {
    sessionsWhereHostWasSeen.delete(sessionId);
    sessionsWhereWeLostConnection.delete(sessionId);
    sessionsWhereWeAnnouncedDrop.delete(sessionId);
    clearPendingPresenceSettle(sessionId);
  };
  const clearPendingHostDisconnect = (sessionId: string): boolean => {
    const timer = pendingHostDisconnectTimers.get(sessionId);
    if (timer !== undefined) {
      clearTimeout(timer);
      pendingHostDisconnectTimers.delete(sessionId);
      return true;
    }
    return false;
  };
  const sessionHostIsConnected = (session: BibleReadingSession): boolean =>
    session.connectedUsers.value.some(
      (user) =>
        isSessionHost(session.options.value, user.userId) ||
        isSessionHost(session.options.value, user.connectionId)
    );
  const sessionWeAreHost = (session: BibleReadingSession): boolean =>
    locallyHostedSessionIds.has(session.id) ||
    session.connectedUsers.value.some(
      (user) =>
        user.isSelf &&
        (isSessionHost(session.options.value, user.userId) ||
          isSessionHost(session.options.value, user.connectionId))
    );
  // Whether this client's view of who's present is worth acting on. We are
  // definitionally present in our own session, so a list that doesn't even
  // include us means the presence channel is broken (it can go permanently
  // silent after a dropped connection) — and "the host isn't in the list"
  // tells us nothing at all. Never eject anyone on that basis.
  const sessionPresenceIsTrustworthy = (
    session: BibleReadingSession
  ): boolean =>
    session.isSynced.value &&
    session.connectedUsers.value.some((user) => user.isSelf);
  // Our own connection and the host's are separate states: a host toast must
  // never supersede a waiting "you rejoined", or someone told they dropped
  // would never hear they're back.
  const sessionSelfToastKey = (sessionId: string): string =>
    `session-self:${sessionId}`;
  const sessionHostToastKey = (sessionId: string): string =>
    `session-host:${sessionId}`;
  effect(() => {
    // Read so this effect re-runs when the self-reconnect settle timer fires.
    const _presenceSettleGeneration = presenceSettleTick.value;
    for (const tab of tabs.tabs.value) {
      const session = tab.sharedSession;
      if (!session) continue;

      if (session.options.value.endedAt !== null) {
        forgetSessionPresence(session.id);
        clearPendingHostDisconnect(session.id);
        tabs.removeTab(tab.id);
        continue;
      }

      const { t } = i18n;

      const hostId = session.options.value.hostUserId;
      const hostIsConnected = hostId ? sessionHostIsConnected(session) : false;
      if (hostIsConnected) {
        sessionsWhereHostWasSeen.add(session.id);
      }

      // Two different failures look similar on the presence list:
      //  - Our own connection dropped: `isSynced` goes false, and/or every
      //    other user disappears at once (the OS reports all peers as
      //    disconnected). Show "you lost/rejoined" toasts.
      //  - Only the host dropped: other guests are still visible. Show
      //    host-specific toasts below, not these.
      // If this device is itself a host (including another of the host's
      // devices), an empty remote list is "someone else left", not "we
      // disconnected" — even if `isSynced` blips when that peer drops.
      //
      // Our own drop clears the WHOLE list, our own entry included (see
      // `rebuildRemoteClientsSubscription` in SessionsManager), so an empty
      // list means us. Still seeing ourselves while everyone else has gone
      // means the presence channel is working fine and they really did
      // leave — one at a time or all at once, it makes no difference.
      const ourConnectionDropped =
        !sessionWeAreHost(session) &&
        sessionsWhereHostWasSeen.has(session.id) &&
        (!session.isSynced.value || session.connectedUsers.value.length === 0);

      if (ourConnectionDropped) {
        clearPendingHostDisconnect(session.id);
        clearPendingPresenceSettle(session.id);
        if (!sessionsWhereWeLostConnection.has(session.id)) {
          sessionsWhereWeLostConnection.add(session.id);
          if (!justResumedFromBackground.value) {
            // Remember that we actually said it, so the matching
            // "you're back" toast below isn't silently dropped.
            sessionsWhereWeAnnouncedDrop.add(session.id);
            toast(
              t("session-disconnected", {
                defaultValue: "You lost connection to the session",
              }),
              { key: sessionSelfToastKey(session.id) }
            );
          }
        }
        continue;
      }

      // Recovery needs positive evidence, not just the absence of the drop
      // signals: right after a resume the list can be empty while `isSynced`
      // already reads true, which is presence still catching up rather than
      // us being back.
      if (
        sessionsWhereWeLostConnection.has(session.id) &&
        session.isSynced.value &&
        session.connectedUsers.value.length > 0
      ) {
        sessionsWhereWeLostConnection.delete(session.id);
        // Having told someone they dropped, always tell them they're back —
        // even inside the post-resume window that suppresses the first
        // toast, otherwise they are left believing they're still offline.
        if (sessionsWhereWeAnnouncedDrop.delete(session.id)) {
          toast(
            t("session-reconnected", {
              defaultValue: "You rejoined the session",
            }),
            { key: sessionSelfToastKey(session.id) }
          );
        }
        if (!locallyHostedSessionIds.has(session.id) && !hostIsConnected) {
          clearPendingPresenceSettle(session.id);
          sessionsSettlingAfterSelfReconnect.add(session.id);
          pendingPresenceSettleTimers.set(
            session.id,
            setTimeout(() => {
              pendingPresenceSettleTimers.delete(session.id);
              sessionsSettlingAfterSelfReconnect.delete(session.id);
              presenceSettleTick.value++;
            }, SELF_RECONNECT_SETTLE_MS)
          );
        }
      }

      if (
        sessionsSettlingAfterSelfReconnect.has(session.id) &&
        !hostIsConnected
      ) {
        // Presence is still catching up after our own reconnect. Skip
        // host disconnect/reconnect toasts until it settles.
        continue;
      }
      if (sessionsSettlingAfterSelfReconnect.has(session.id)) {
        clearPendingPresenceSettle(session.id);
      }

      // Never auto-close a session this client created — the host is the
      // local user, and any apparent host disconnect is an identity flip
      // (login/logout), not a real departure.
      if (locallyHostedSessionIds.has(session.id)) continue;

      if (!hostId) continue;

      // A session stays alive as long as the host OR any co-host is present,
      // so appointing a co-host lets the original host leave without kicking
      // everyone else out.
      if (hostIsConnected) {
        // Host came back (e.g. reconnected after their login flow) — cancel
        // any pending close so the tab survives the round-trip.
        if (clearPendingHostDisconnect(session.id)) {
          toast(
            t("session-host-reconnected", {
              defaultValue: "The host reconnected to the session",
            }),
            { key: sessionHostToastKey(session.id) }
          );
        }
      } else if (
        sessionsWhereHostWasSeen.has(session.id) &&
        !pendingHostDisconnectTimers.has(session.id) &&
        sessionPresenceIsTrustworthy(session) &&
        !justResumedFromBackground.value
      ) {
        // Host appears to have left, but it may be a transient reconnect
        // (host logging in/out) — wait briefly to give the CRDT time to
        // deliver a new `hostUserId` or for the host's connection to
        // re-appear before tearing down. We only get here once THIS
        // client's own connection is synced and past its post-resume grace
        // window, so this reading is trustworthy enough to start the timer
        // (though it's re-verified again below right before acting on it).
        const tabId = tab.id;
        const sessionId = session.id;
        toast(
          t("session-host-disconnected", {
            defaultValue: "The host disconnected from the session",
          }),
          { key: sessionHostToastKey(sessionId) }
        );
        const timer = setTimeout(() => {
          pendingHostDisconnectTimers.delete(sessionId);
          const currentTab = tabs.tabs.value.find(
            (candidateTab) => candidateTab.id === tabId
          );
          const currentSession = currentTab?.sharedSession;
          if (!currentSession || currentSession.id !== sessionId) {
            // Tab/session already gone (e.g. closed some other way).
            forgetSessionPresence(sessionId);
            return;
          }
          if (
            !sessionPresenceIsTrustworthy(currentSession) ||
            sessionHostIsConnected(currentSession)
          ) {
            // Our own connection is still resyncing, our presence view is
            // unreliable, or the host is actually back — don't tear down on
            // stale/incomplete information. The effect above will
            // re-evaluate and re-arm this timer if the host is still
            // genuinely gone once presence is trustworthy again.
            return;
          }
          forgetSessionPresence(sessionId);
          toast(
            t("session-host-left", {
              defaultValue: "The host left — you were removed from the session",
            })
          );
          tabs.removeTab(tabId);
        }, HOST_DISCONNECT_GRACE_MS);
        pendingHostDisconnectTimers.set(sessionId, timer);
      }
    }
  });

  // When the local user's identity changes (login/logout) and they host
  // sessions, push the new identity into each session's `hostUserId` via
  // the CRDT. Without this update, joiners' host-detection would still be
  // looking for the host's previous (anonymous) connection id and would
  // never re-acquire them as host on the new connection.
  effect(() => {
    const newLoginUserId = login.userId.value;
    const localConnectionId = os.connectionId;
    const desiredHostId = newLoginUserId ?? localConnectionId;
    if (!desiredHostId) return;
    for (const tab of tabs.tabs.value) {
      const session = tab.sharedSession;
      if (!session) continue;
      if (!locallyHostedSessionIds.has(session.id)) continue;
      if (session.options.value.hostUserId === desiredHostId) continue;
      try {
        session.updateOptions({ hostUserId: desiredHostId });
      } catch {
        // Best-effort — joiners will fall back to their grace-period
        // timeout if the update can't be delivered.
      }
    }
  });

  const handleCreateSharedSession = async () => {
    closeSidebarAndSettings();
    // Start the session where the user is reading, not at the default book.
    const activeReadingState = selectedTab.value?.readingState ?? null;
    const session = await sessions.createSession(
      activeReadingState
        ? getSessionStartPosition(activeReadingState)
        : undefined
    );
    captureEvent("create_session", {
      sessionId: session.id,
    });
    locallyHostedSessionIds.add(session.id);
    wrapSessionLifecycle(session);
    // Auto-publish: the moment a shared tab is created, other logged-in
    // users see it in their sidebar and can click to join — no manual
    // invite/notification step.
    void invitations.publishSession(session);
    const tab = tabs.addTab(session);
    tabsLayout.setSelectedSlotTab(tab.id);
    return session;
  };

  const handleJoinSharedSession = async (
    id: string,
    startPosition?: SessionStartPosition
  ) => {
    closeSidebarAndSettings();
    const session = await sessions.joinSession(id, startPosition);
    captureEvent("join_session", {
      sessionId: session.id,
    });
    wrapSessionLifecycle(session);
    const tab = tabs.addTab(session);
    tabsLayout.setSelectedSlotTab(tab.id);
    return session;
  };

  const handleOpenVerseReference = async (ref: VerseRef) => {
    panes.closeFullscreenPanes();
    let tab = selectedTab.value;

    if (!tab) {
      tab = tabs.tabs.value[0] ?? null;
    }

    if (tab) {
      const translationid =
        tab.readingState.translationId.value ??
        tab.readingState.defaultTranslation.id;
      await tab.readingState.selectTranslationAndChapter(
        translationid,
        ref.book,
        ref.chapter,
        {
          scrollToVerse: ref.verse,
        }
      );
    } else {
      tab = tabs.addTab(undefined, {
        initialBookId: ref.book,
        initialChapterNumber: ref.chapter,
        scrollToVerse: ref.verse,
      });
    }

    if (ref.verse) {
      const verses = ref.endVerse
        ? range(ref.verse, ref.endVerse + 1)
        : ref.verse;
      tab.readingState.decorateVerses(ref.book, ref.chapter, verses, {
        className: "sb-verse-decoration-diminish",
        containerClassName: "sb-chapter-decoration-diminish",
        removeAfterMs: 3000,
      });
    }
  };

  const handleOpenChat = (sharedChat: ChatSession) => {
    sidebar.openChatPanel();
    chats.selectChat(sharedChat.id);
  };

  const invitations = createInvitationsManager(
    os,
    login,
    friends,
    async (sessionId) => {
      await handleJoinSharedSession(sessionId);
    }
  );

  const setupInitialSession = async () => {
    // Joining a session opens a live WebSocket — never do this during SSR.
    if (typeof window === "undefined") {
      return;
    }
    const initialSessionId =
      navigation.currentUrl.value.searchParams.get("sessionId");
    if (!initialSessionId) {
      return;
    }

    const linkedPosition = readInitialReadingParams(
      navigation.initialUrl,
      navigation.basePath
    ).specified;
    const bootTab = tabs.tabs.peek()[0] ?? null;
    const startPosition =
      linkedPosition && bootTab
        ? getSessionStartPosition(bootTab.readingState)
        : undefined;

    const whenTabsRestored = (run: () => void) => {
      if (tabsRestored.peek()) {
        run();
        return;
      }
      const stopWaiting = tabsRestored.subscribe((restored) => {
        if (restored) {
          stopWaiting();
          run();
        }
      });
    };

    try {
      await handleJoinSharedSession(initialSessionId, startPosition);
    } catch (error) {
      console.error("Failed to join the session from the URL:", error);
      // Destructured for the translation lint rules, which only recognise a
      // bare `t` — see the sign-out toast below.
      const { t } = i18n;
      toast(
        t("session-join-failed", {
          defaultValue: "Couldn't join the shared session",
        })
      );
      // Restoring saved tabs skipped the link's chapter, leaving it for the
      // session to show. With no session, show it in a normal tab instead.
      if (startPosition) {
        whenTabsRestored(() => showLinkedChapter(startPosition));
      }
      return;
    }

    if (!startPosition || !bootTab) {
      return;
    }

    whenTabsRestored(() => {
      if (tabs.tabs.peek().includes(bootTab)) {
        tabs.removeTab(bootTab.id);
      }
    });
  };

  const showLinkedChapter = (position: SessionStartPosition) => {
    const tab =
      tabs.tabs
        .peek()
        .find(
          (candidate) =>
            !candidate.sharedSession &&
            candidate.readingState.bookId.peek() === position.initialBookId &&
            candidate.readingState.chapterNumber.peek() ===
              position.initialChapterNumber
        ) ?? tabs.addTab(undefined, position);
    tabs.selectTab(tab.id);
    tabsLayout.setSelectedSlotTab(tab.id);
  };

  // Tell the user when we signed them out for them. `login.sessionEnded` only fires
  // when a forced sign-out actually happened, so this can't toast for a request that
  // merely failed, nor for a sign-out the user asked for. Without a message they
  // would just watch their highlights and saves vanish with no explanation.
  effect(() => {
    const ended = login.sessionEnded.value;
    if (!ended || typeof window === "undefined") {
      return;
    }

    // Destructured rather than called as `i18n.t(...)`: the translation lint rules
    // only recognise calls to a bare `t`, and `translation-unused-keys` is
    // auto-fixable, so `i18n.t("...")` would get these keys deleted from en.json.
    const { t } = i18n;
    toast(
      ended.reason === "account_suspended"
        ? t("account-suspended-message", {
            defaultValue: "Your account has been suspended.",
          })
        : t("signed-out-message", {
            defaultValue: "You've been signed out. Please sign in again.",
          })
    );
  });

  // Ask which version to keep whenever a sync pass finds a note that changed in
  // two places. Nothing is overwritten until the user answers, so this prompt is
  // the only thing standing between a queued offline edit and someone else's
  // writing.
  effect(() => {
    if (typeof window === "undefined") {
      return;
    }
    syncAnnotationConflictModal(modals, annotations.sync, toast);
  });

  // Say something when a note can't be saved to the account. The composer now
  // closes as soon as the note is on the device, so without this a server
  // refusal would only ever appear in the console — the note would look saved.
  let reportedSyncErrors = 0;
  effect(() => {
    const count = annotations.sync.syncErrors.value.size;
    const isNew = count > reportedSyncErrors;
    reportedSyncErrors = count;
    if (!isNew || typeof window === "undefined") {
      return;
    }
    // Destructured for the translation lint rules, which only recognise a bare
    // `t` — see the sign-out toast above.
    const { t } = i18n;
    toast(
      t("annotation-sync-failed", {
        defaultValue:
          "Couldn't save a note to your account. It's still on this device.",
      })
    );
  });

  const handleOpenDiscover = () => {
    if (!playlists.view.peek()) {
      playlists.view.value = playlists.playing.peek()
        ? "play_playlist"
        : "discover";
    } else {
      playlists.view.value = null;
    }
  };
  const handleCloseDiscover = () => {
    playlists.view.value = null;
  };

  effect(() => {
    const isPlaying = !!playlists.playing.value;
    if (isPlaying && !isMobile.value) {
      playlists.view.value = playlists.playing.peek()
        ? "play_playlist"
        : "discover";
    }
  });

  /**
   * Builds the AI tools that let a provider interact with the core app state.
   */
  const getCoreTools = () => {
    const goToReference = generateFunctionTool({
      name: "goToReference",
      description:
        "Navigates the user to a specific book, chapter, and verse in the Bible.",
      parameters: AIBibleVerseRefSchema,
      function: async (args) => {
        const readingState = currentReadingState.peek();
        if (!readingState) {
          return "error: no reading state available";
        }

        await readingState.tab.readingState.selectTranslationAndChapter(
          readingState.translationId,
          args.ref.bookId,
          args.ref.chapter,
          {
            scrollToVerse: args.ref.verse ?? undefined,
          }
        );

        if (args.ref.verse) {
          readingState.tab.readingState.decorateVerses(
            args.ref.bookId,
            args.ref.chapter,
            args.ref.endVerse
              ? range(args.ref.verse, args.ref.endVerse + 1)
              : args.ref.verse,
            {
              className: "sb-verse-decoration-diminish",
              containerClassName: "sb-chapter-decoration-diminish",
              removeAfterMs: 3000,
            }
          );
        }

        return "success";
      },
    });

    const searchVerses = generateFunctionTool({
      name: "searchVerses",
      description:
        "Searches the Bible for verses matching the given query. Returns a list of results.",
      parameters: z.object({
        q: z.string().describe("The search query to look for in the Bible."),
      }),
      function: async (args) => {
        const readingState = currentReadingState.peek()?.tab.readingState;
        if (!readingState) {
          return "error: no reading state available";
        }

        const activeLanguage =
          readingState?.translation.value?.language ??
          readingState?.defaultTranslation.language ??
          getDefaultTranslationForLanguage(i18n.defaultLanguage).language;
        const results = await search.searchVerses(
          activeLanguage,
          readingState.translationId.peek(),
          args.q
        );

        const verses = (results.hits ?? []).map((hit) => ({
          translationId: hit.document.translation,
          translationLabel: hit.document.translation,
          bookId: hit.document.book,
          bookLabel: hit.document.book,
          chapterNumber: hit.document.chapter,
          verseNumber: hit.document.verse,
          text: hit.document.text,
        }));

        return JSON.stringify(verses);
      },
    });

    const createPlaylist = generateFunctionTool({
      name: "createPlaylist",
      description:
        "Opens the playlist editor pre-filled with the given generated playlist so the user can review, edit, and save it themselves. Does not play or save anything on its own. Each call opens a brand-new, independent playlist that has no relationship to any playlist discussed earlier in this conversation (including one already open in the editor) — it is never used to modify an existing playlist.",
      parameters: GeneratedPlaylistSchema,
      function: async (args) => {
        let items: PlaylistItemData[];
        try {
          items = args.items.map((i) => convertToPlaylistItem(i));
        } catch (err) {
          return `error: ${err instanceof Error ? err.message : String(err)}`;
        }

        const editing = await playlists.createNewPlaylist({
          title: args.title,
          description: args.description,
          items,
        });
        if (!editing) {
          return "error: could not open the playlist editor — sign-in was required and did not complete";
        }

        return "success";
      },
    });

    const translationTools = createTranslationAgentTools({
      loadCatalog: async () => {
        // A chapter load only merges the one translation being read. Searching
        // that partial list would hide every other language.
        if (data.catalogLoaded.peek()) {
          return data.availableTranslations.peek();
        }
        return data.getTranslations();
      },
      showTranslationSuggestion: (suggestion, call) => {
        const openChats = chats.chats.peek();
        const callingChat = call?.chatId
          ? openChats.find((chat) => chat.id === call.chatId)
          : undefined;
        // A chat id that is not open must not fall through to whichever chat
        // the reader happens to be looking at.
        if (call?.chatId && !callingChat) {
          throw new Error("No chat is open.");
        }
        const chat = callingChat ?? chats.selectedChat.peek();
        if (!chat) {
          throw new Error("No chat is open.");
        }
        chat.translationSuggestion.value = suggestion;
      },
    });

    return [
      goToReference.tool,
      searchVerses.tool,
      createPlaylist.tool,
      ...translationTools,
    ];
  };

  const enableCoreChatContext = () => {
    chats.addContext({
      id: "core",
      label: { key: "seed-bible", defaultValue: "Seed Bible" },
      tools: getCoreTools(),
    });
  };

  enableCoreChatContext();

  // // When the app is opened via a shared `?playlist={recordName}.{id}` link,
  // // load that playlist and start playing it immediately. The locator's `id` is
  // // always `playlist_<uuid>` (never contains a dot), so we split on the LAST dot
  // // to stay correct even when the `recordName` itself contains dots.
  // const setupInitialPlaylist = async () => {
  //   // Loading/playing touches the network and the reader — never during SSR.
  //   if (typeof window === "undefined") {
  //     return;
  //   }
  //   const locator = navigation.currentUrl.value.searchParams.get("playlist");
  //   if (!locator) {
  //     return;
  //   }
  //   const lastDot = locator.lastIndexOf(".");
  //   if (lastDot <= 0 || lastDot === locator.length - 1) {
  //     console.error("Invalid playlist locator:", locator);
  //     return;
  //   }
  //   const recordName = locator.slice(0, lastDot);
  //   const id = locator.slice(lastDot + 1);
  //   try {
  //     const playlist = await playlists.loadPlaylist(recordName, id);
  //     playlists.startPlaying(playlist);
  //     handleOpenDiscover();
  //   } catch (error) {
  //     console.error("Failed to load playlist from URL:", error);
  //     const { t } = i18n;
  //     toast(
  //       t("failed-to-load-playlist", {
  //         defaultValue: "Failed to load playlist",
  //       })
  //     );
  //   }
  // };

  // Run the playlist setup after the session join: `startPlaying` prefers a
  // shared-session tab, so a link carrying both `?sessionId=` and `?playlist=`
  // should target the session tab created by the join.
  void setupInitialSession();
  //.then(() => setupInitialPlaylist());

  void setupFriendLinks({ navigation, login, friends, modals, toast });

  // A shared `?readingPlan=` link loads the plan, then opens the pane once a
  // reading tab is actually there. The tab is usually ready after the network
  // round-trip, but if it isn't yet this waits rather than selecting the plan
  // and leaving the pane closed with no explanation.
  const pendingSharedPlan = signal<ReadingPlan | null>(null);
  effect(() => {
    const plan = pendingSharedPlan.value;
    if (!plan) {
      return;
    }
    const readingState = selectedTab.value?.readingState;
    if (!readingState) {
      return;
    }
    pendingSharedPlan.value = null;
    openReadingPlansPane({
      readingPlans,
      readingState,
      panesManager: panes,
      modals,
      playlists,
      bibleData: data,
      friends,
      os,
      login,
      gallery,
      toast,
    });
    showReadingPlanDetailView();
  });

  const setupInitialReadingPlan = async () => {
    if (typeof window === "undefined") {
      return;
    }
    const locator = navigation.currentUrl.value.searchParams.get("readingPlan");
    if (!locator) {
      return;
    }
    // Destructured rather than called as `i18n.t(...)`: the translation lint
    // rules only recognise calls made through a bare `t`.
    const { t } = i18n;
    try {
      const plan = await readingPlans.loadByLocator(locator);
      if (!plan) {
        toast(
          t("failed-to-load-reading-plan", {
            defaultValue: "Failed to load reading plan",
          })
        );
        return;
      }
      pendingSharedPlan.value = plan;
    } catch (error) {
      console.error("Failed to load reading plan from URL:", error);
      toast(
        t("failed-to-load-reading-plan", {
          defaultValue: "Failed to load reading plan",
        })
      );
    }
  };
  void setupInitialReadingPlan();

  // Constructed here rather than beside the other managers because it needs
  // `currentReadingState`, which is defined well below them.
  const today = createTodayManager({
    os,
    login,
    friends,
    navigation,
    search,
    bibleData: data,
    annotations,
    defaultLanguage: i18n.defaultLanguage,
    currentReadingState,
  });

  const state: SeedBibleState = {
    os,
    bibleData: data,
    theme: {
      ...themeManager,
      themeCssVariables,
      themeCssClasses,
      googleFontFamiliesToLoad,
    },
    customizations,
    sidebar,
    tabs,
    tabsLayout,
    panes,
    selector,
    tools,
    login,
    readingHistory,
    highlights,
    saves,
    bookmarks,
    friends,
    annotations,
    chats,
    sessions,
    modals,
    settings,
    invitations,
    search,
    navigation,
    i18n,
    discover,
    today,
    readingExtensions,
    extensions,
    extensionSettings,
    readingPlans,
    playlists,
    gallery,
    textToSpeech,
    audioPlayback,
    tutorial,
    onboarding,
    yourContent,
    isYourContentOpen,
    openYourContent,
    closeYourContent,
    isPlaylistHistoryOpen,
    openPlaylistHistory,
    closePlaylistHistory,
    isFriendsOpen,
    openFriends,
    closeFriends,
    isExtensionsOpen,
    extensionsPageEntryId,
    openExtensions,
    closeExtensions,
    isProfileOpen,
    openProfile,
    closeProfile,
    isEditProfileOpen,
    openEditProfile,
    closeEditProfile,
    isTermsOpen,
    openTerms,
    closeTerms,
    isPrivacyOpen,
    openPrivacy,
    closePrivacy,
    isCodeOfConductOpen,
    openCodeOfConduct,
    closeCodeOfConduct,
    features,
    dispose: () => {
      // Aborts each tab's open chapter request. Without this an SSR render
      // that hit its deadline leaves its fetch subscribed, so a hung request
      // stays in the shared response cache for the next render to adopt.
      for (const tab of tabs.tabs.value) {
        tab.readingState.dispose();
      }
      i18n.dispose();
      disposeReadingTicker();
    },
    app: {
      createSharedSession: handleCreateSharedSession,
      joinSharedSession: handleJoinSharedSession,
      panelsEnabled,
      selectedTab,
      effectiveSlots,
      effectiveSlotLayout,
      effectivePanes,
      viewportWidth,
      viewportHeight,
      applyViewport,
      hydrateFromStorage,
      isMobile,
      isMinimalEmbed,
      isCompactReader,
      isMobileLandscape,
      isCompactDesktop,
      currentReadingState,
      selectTab: handleSelectTab,
      addTab: handleAddTab,
      openInNewSlot: handleOpenInNewSlot,
      selectSlot: handleSelectSlot,
      selectPane: handleSelectPane,
      closeFullscreenPanes: panes.closeFullscreenPanes,
      openVerseReference: handleOpenVerseReference,
      openChat: handleOpenChat,
      title,
      description,
      siteName,
      customizationLogoUrl,
      socialImage,
      canonicalUrl,
      socialTitle,
      isAboutPage,
      startSharedPage,
      resumeSharedPage,
      currentToast,
      toast,
      isDiscoverOpen: playlists.isDiscoverOpen,
      openDiscover: handleOpenDiscover,
      closeDiscover: handleCloseDiscover,
    },
  };

  // Discover is rendered as the app's single managed side pane, mirrored from
  // `playlists.view` (defined here rather than next to `handleOpenDiscover`
  // above so the pane's `component` thunk can close over the finished `state`).
  // `view` stays the source of truth for both whether Discover is open and
  // which sub-view shows inside it; DiscoverPane routes discover/create/play
  // internally off the same signal.
  const DISCOVER_PANE_ID = "discover-pane";

  // view -> pane: open (or refresh, by reusing the id) while a view is set,
  // close when it clears. The pane commands read pane state via peek()
  // internally (see PanesManager), so this effect does NOT subscribe to
  // `panes` — closing the pane below (which mutates `panes`) can't retrigger
  // this effect and re-open the pane mid-update, which would trip preact's
  // "Cycle detected". The pane -> view effect below clears `view` on close.
  effect(() => {
    if (playlists.view.value) {
      panes.openPane({
        id: DISCOVER_PANE_ID,
        placement: "side",
        title: () => (
          <DiscoverPaneTitle
            playlists={playlists}
            annotations={annotations}
            tabs={tabs}
            chats={chats}
            openChatPanel={sidebar.openChatPanel}
            modals={modals}
          />
        ),
        header: () => (
          <DiscoverPaneHeader playlists={playlists} annotations={annotations} />
        ),
        onClose: (reason) => {
          if (reason !== "user") {
            return;
          }
          const currentlyPlaying = playlists.playing.value;
          if (currentlyPlaying) {
            playlists.stopPlaying();
          }
        },
        component: () => (
          <DiscoverPane
            state={state}
            tabs={tabs}
            playlists={playlists}
            annotations={annotations}
            modals={modals}
            toast={state.app.toast}
          />
        ),
      });
    } else {
      panes.closePane(DISCOVER_PANE_ID); // no-op when already closed
    }
  });

  // pane -> view: when the pane is closed via its header, or replaced by
  // another side pane (only one side pane may be open at a time), clear the
  // view so the toggle re-opens it on the next click. `peek()` keeps this from
  // looping against the effect above.
  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === DISCOVER_PANE_ID
    );
    if (!paneOpen && playlists.view.peek()) {
      playlists.view.value = null;
    }
  });

  // Today is the app's fullscreen "home" pane, mirrored from `today.isOpen`
  // (which is itself bound to `?today=`). Same reconciling-effect pair as
  // Discover above, and here too the pane -> state direction must `peek()` so
  // closing the pane can't retrigger the opening effect.
  //
  // The thunks are hoisted out of the effect so their identity is stable —
  // rebuilding them on every reopen would remount the whole Today tree.
  // Hoisted out of the render thunk so their identity is stable — rebuilding
  // them per render would defeat the `memo` on `TodayPane`.
  const openTodayBookSelector = () => {
    const slot =
      tabsLayout.slots.value.find(
        (candidate) => candidate.id === tabsLayout.selectedSlotId.value
      ) ?? null;
    if (slot) {
      selector.setOpen(true, slot);
    }
  };
  const renderTodayPane = () => (
    <TodayPane
      today={today}
      login={login}
      theme={themeManager.currentTheme}
      isMobile={isMobile}
      bookmarks={bookmarks.bookmarks}
      onOpenPassage={(target) => openTodayPassage(state, today, target)}
      onOpenBookSelector={openTodayBookSelector}
      onTakeTour={() => tutorial.acceptPrompt()}
      onClose={() => today.close()}
    />
  );
  const renderTodayPaneTitle = () => <TodayPaneTitle />;

  effect(() => {
    if (today.isOpen.value) {
      panes.openPane({
        id: TODAY_PANE_ID,
        placement: "fullscreen",
        title: renderTodayPaneTitle,
        component: renderTodayPane,
        // Today's own close button (TodayCloseButton) stands in for the
        // header's.
        showHeader: false,
      });
    } else {
      panes.closePane(TODAY_PANE_ID); // no-op when already closed
    }
  });

  // Today is the fullscreen home screen, so anything else opening replaces it,
  // and this is where that is enforced. Two ways it can happen:
  //
  // - Today's own pane went away, because another fullscreen pane displaced it
  //   or its close button was used.
  // - Another pane is open alongside Today's. `PanesManager` only clears the
  //   other panes for a pane that fills the screen; a "side" pane replaces
  //   just the previous side pane, so Discover and Reading plans used to open
  //   underneath Today with nothing to tell the user they were there.
  effect(() => {
    const openPanes = panes.panes.value;
    const todayPaneOpen = openPanes.some((pane) => pane.id === TODAY_PANE_ID);
    const anotherPaneOpen = openPanes.some((pane) => pane.id !== TODAY_PANE_ID);
    if (today.isOpen.peek() && (!todayPaneOpen || anotherPaneOpen)) {
      today.close();
    }
  });

  // The Profile screen, wired the same way as Today above: a fullscreen pane
  // mirrored from `isProfileOpen`, with the thunks hoisted so their identity
  // stays stable across reopens.
  //
  // Opening any fullscreen pane closes the others, so the screens reached from
  // Profile ("Edit profile", "Your content", "Friends", playlist history) each
  // carry a back button that reopens it rather than relying on a pane stack.
  const backToProfile = () => {
    closeEditProfile();
    closeYourContent();
    closeFriends();
    closePlaylistHistory();
    openProfile();
  };
  const renderProfileBackButton = () => (
    <ProfileBackButton onBack={backToProfile} />
  );
  const editProfilePicture = () => {
    // Destructured for the same reason as the toast below: the translation
    // lint rules only recognise calls made through a bare `t`.
    const { t } = i18n;
    openProfilePictureModal({ modals, login, t });
  };
  /**
   * Opens the plans pane from one of the Profile screens. False when there is
   * no reader to open it beside, in which case nothing has changed on screen.
   * `reopen` brings back the screen it came from, which this closes, so the
   * pane's back button can return there instead of dropping to the reader.
   */
  const openReadingPlansFullscreen = (reopen: () => void): boolean => {
    const readingState = selectedTab.peek()?.readingState;
    if (!readingState) {
      return false;
    }
    closeYourContent();
    closeProfile();
    // Fullscreen rather than the toolbar's docked "side": the user came from a
    // fullscreen screen, so a side panel would leave them looking at the reader.
    openReadingPlansPane({
      readingPlans,
      readingState,
      panesManager: panes,
      modals,
      playlists,
      bibleData: data,
      friends,
      os,
      login,
      gallery,
      placement: "fullscreen",
      toast,
      onBack: () => {
        panes.closePane(READING_PLANS_PANE_ID);
        reopen();
      },
    });
    return true;
  };
  const openReadingPlansFromProfile = () => {
    openReadingPlansFullscreen(openProfile);
  };
  const continuePlaylistFromProfile = (
    entry: PlaylistPlayHistory
  ): Promise<void> => {
    // Stay on the profile until the playlist has loaded. Closing first drops
    // the user on the reader with no sign that anything is happening, and a
    // failed load only toasts after they've already left.
    const { t } = i18n;
    return playlists.playFromHistory(entry).then(
      () => {
        closeProfile();
      },
      (error) => {
        toast(playlistHistoryOpenFailedMessage(t));
        throw error;
      }
    );
  };
  const renderProfilePane = () => (
    <ProfilePane
      state={state}
      onEditProfile={openEditProfile}
      onEditPicture={editProfilePicture}
      onOpenReadingPlans={openReadingPlansFromProfile}
      onOpenPlaylistHistory={openPlaylistHistory}
      onContinuePlaylist={continuePlaylistFromProfile}
      onOpenYourContent={openYourContent}
      onOpenFriends={openFriends}
    />
  );
  const renderProfilePaneTitle = () => <ProfilePaneTitle />;

  effect(() => {
    if (isProfileOpen.value) {
      panes.openPane({
        id: PROFILE_PANE_ID,
        placement: "fullscreen",
        title: renderProfilePaneTitle,
        component: renderProfilePane,
      });
    } else {
      panes.closePane(PROFILE_PANE_ID); // no-op when already closed
    }
  });

  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === PROFILE_PANE_ID
    );
    if (!paneOpen && isProfileOpen.peek()) {
      closeProfile();
    }
  });

  // "Edit profile", reached from the pencil on the profile card or from the
  // name itself.
  const renderEditProfilePane = () => (
    <EditProfilePane state={state} onEditPicture={editProfilePicture} />
  );
  const renderEditProfilePaneTitle = () => <EditProfilePaneTitle />;

  effect(() => {
    if (isEditProfileOpen.value) {
      panes.openPane({
        id: EDIT_PROFILE_PANE_ID,
        placement: "fullscreen",
        title: renderEditProfilePaneTitle,
        leading: renderProfileBackButton,
        component: renderEditProfilePane,
      });
    } else {
      panes.closePane(EDIT_PROFILE_PANE_ID); // no-op when already closed
    }
  });

  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === EDIT_PROFILE_PANE_ID
    );
    if (!paneOpen && isEditProfileOpen.peek()) {
      closeEditProfile();
    }
  });

  // "Your content", the third fullscreen screen, wired like the two above.
  // Each handler closes the screen first: all three destinations (reader,
  // playlist player, annotation editor) sit behind it.
  const openPassageFromContent = (target: TodayPassageTarget) => {
    closeYourContent();
    closeProfile();
    openTodayPassage(state, today, target);
  };
  const playPlaylistFromContent = (playlist: Playlist) => {
    closeYourContent();
    closeProfile();
    playlists.startPlaying(playlist, 0);
  };
  const editPlaylistFromContent = (playlist: Playlist) => {
    closeYourContent();
    closeProfile();
    // `editPlaylist` only sets the Discover pane's view, and that pane docks
    // beside the reader — so the fullscreen screens above it have to close
    // first, or the editor opens behind them.
    playlists.editPlaylist(playlist);
  };
  const editAnnotationFromContent = (annotation: Annotation) => {
    closeYourContent();
    closeProfile();
    // Opens the Discover pane's annotation editor, the same one the reader
    // uses — rather than growing a second editor on this screen.
    annotations.editAnnotation(annotation);
  };
  const createPlaylistFromContent = () => {
    closeYourContent();
    closeProfile();
    void playlists.createNewPlaylist();
  };
  // The customization editor is a side pane beside the reader, so the
  // fullscreen screens above it close first, as for the playlist editor.
  const editCustomizationFromContent = (customizationId: string) => {
    closeYourContent();
    closeProfile();
    openCustomizationEditPane(state, customizationId);
  };
  const createCustomizationFromContent = async () => {
    closeYourContent();
    closeProfile();
    let created;
    try {
      created = await customizations.create();
    } catch (error) {
      console.error("Error creating customization:", error);
      // Destructured for the translation lint rules, as in the toast above.
      const { t } = i18n;
      toast(
        t("customization-create-failed", {
          defaultValue:
            "Couldn't create your Custom Branding. Please try again.",
        })
      );
      return;
    }
    openCustomizationEditPane(state, created.id);
    // First-time-only overview: the customization name and logo, then a
    // deep-linked tour of the new theme's editor sections.
    tutorial.startContextual(
      "customization-created",
      buildCustomizationTutorialSteps(state, created.defaultVariantId)
    );
  };
  // Both land in the plans pane, opened fullscreen the way the Profile card
  // opens it, and then drill straight into the plan so the user doesn't have
  // to find it in the list a second time.
  const openReadingPlanFromContent = (plan: ReadingPlanMetadata) => {
    if (!openReadingPlansFullscreen(openYourContent)) {
      return;
    }
    // A draft has nothing to read yet, so it picks up where the author left
    // off — in the editor — rather than opening an empty detail view.
    if (plan.status === "draft") {
      void openReadingPlanEditor(readingPlans, plan, {
        fromAnotherScreen: true,
      });
    } else {
      void openReadingPlanDetail(readingPlans, plan, {
        fromAnotherScreen: true,
      });
    }
  };
  const editReadingPlanFromContent = (plan: ReadingPlanMetadata) => {
    if (!openReadingPlansFullscreen(openYourContent)) {
      return;
    }
    void openReadingPlanEditor(readingPlans, plan, { fromAnotherScreen: true });
  };
  const renderYourContentPane = () => (
    <YourContentPane
      state={state}
      onOpenPassage={openPassageFromContent}
      onPlayPlaylist={playPlaylistFromContent}
      onEditPlaylist={editPlaylistFromContent}
      onEditAnnotation={editAnnotationFromContent}
      onCreatePlaylist={createPlaylistFromContent}
      onOpenReadingPlan={openReadingPlanFromContent}
      onEditReadingPlan={editReadingPlanFromContent}
      onEditCustomization={editCustomizationFromContent}
      onCreateCustomization={() => void createCustomizationFromContent()}
    />
  );
  const renderYourContentPaneTitle = () => <YourContentPaneTitle />;

  effect(() => {
    if (isYourContentOpen.value) {
      panes.openPane({
        id: YOUR_CONTENT_PANE_ID,
        placement: "fullscreen",
        title: renderYourContentPaneTitle,
        leading: renderProfileBackButton,
        component: renderYourContentPane,
      });
    } else {
      panes.closePane(YOUR_CONTENT_PANE_ID); // no-op when already closed
    }
  });

  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === YOUR_CONTENT_PANE_ID
    );
    if (!paneOpen && isYourContentOpen.peek()) {
      closeYourContent();
    }
  });

  // Playlist history, another fullscreen screen reached from Profile.
  // Playing a row leaves once playback has started, so the reader (and, on
  // desktop, the playlist player) is what's in front.
  const leavePlaylistHistory = () => {
    closePlaylistHistory();
    closeProfile();
  };
  const renderPlaylistHistoryPane = () => (
    <PlaylistHistoryPane state={state} onLeave={leavePlaylistHistory} />
  );
  const renderPlaylistHistoryPaneTitle = () => <PlaylistHistoryPaneTitle />;

  effect(() => {
    if (isPlaylistHistoryOpen.value) {
      panes.openPane({
        id: PLAYLIST_HISTORY_PANE_ID,
        placement: "fullscreen",
        title: renderPlaylistHistoryPaneTitle,
        leading: renderProfileBackButton,
        component: renderPlaylistHistoryPane,
      });
    } else {
      panes.closePane(PLAYLIST_HISTORY_PANE_ID); // no-op when already closed
    }
  });

  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === PLAYLIST_HISTORY_PANE_ID
    );
    if (!paneOpen && isPlaylistHistoryOpen.peek()) {
      closePlaylistHistory();
    }
  });

  // "Friends", wired like "Your content" above.
  const renderFriendsPane = () => <FriendsPane state={state} />;
  const renderFriendsPaneTitle = () => <FriendsPaneTitle />;

  effect(() => {
    if (isFriendsOpen.value) {
      panes.openPane({
        id: FRIENDS_PANE_ID,
        placement: "fullscreen",
        title: renderFriendsPaneTitle,
        leading: renderProfileBackButton,
        component: renderFriendsPane,
      });
    } else {
      panes.closePane(FRIENDS_PANE_ID); // no-op when already closed
    }
  });

  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === FRIENDS_PANE_ID
    );
    if (!paneOpen && isFriendsOpen.peek()) {
      closeFriends();
    }
  });

  const renderExtensionsPane = () => <ExtensionsPage state={state} />;
  const renderExtensionsPaneTitle = () => <ExtensionsPaneTitle />;
  const renderExtensionsPaneLeading = () => (
    <ExtensionsPaneBackButton state={state} />
  );

  effect(() => {
    if (isExtensionsOpen.value) {
      panes.openPane({
        id: EXTENSIONS_PANE_ID,
        placement: "fullscreen",
        title: renderExtensionsPaneTitle,
        leading: renderExtensionsPaneLeading,
        component: renderExtensionsPane,
      });
    } else {
      panes.closePane(EXTENSIONS_PANE_ID); // no-op when already closed
    }
  });

  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === EXTENSIONS_PANE_ID
    );
    if (!paneOpen && isExtensionsOpen.peek()) {
      closeExtensions();
    }
  });

  const renderAboutPane = () => <AboutPage state={state} />;
  const renderAboutPaneTitle = () => <AboutPaneTitle />;

  effect(() => {
    if (isAboutPage.value) {
      panes.openPane({
        id: ABOUT_PANE_ID,
        placement: "fullscreen",
        title: renderAboutPaneTitle,
        component: renderAboutPane,
        // The page's own hero card names it.
        showHeader: false,
      });
    } else {
      panes.closePane(ABOUT_PANE_ID); // no-op when already closed
    }
  });

  // Unlike Today's `isOpen` (a plain writable boolean), `isAboutPage` is a
  // read-only computed derived from the URL — closing the pane can't just
  // flip a signal back. Instead, leave "/about" for the current tab's
  // reading position, mirroring how a genuine tab-focus change already does
  // (see TabsManager.leaveStaticPage). This fires whether the pane was
  // closed via its header's close button, `closeFullscreenPanes()` (e.g.
  // selecting a tab), or displacement by another fullscreen pane.
  effect(() => {
    const paneOpen = panes.panes.value.some(
      (pane) => pane.id === ABOUT_PANE_ID
    );
    if (!paneOpen && isAboutPage.peek()) {
      tabs.leaveStaticPage();
    }
  });

  /**
   * Leaves the reading plan page for the plan in the plans pane: its pace
   * picker when `progress` is null (a fresh start), else the reader's place
   * in that progress, ready to read the day or session they're on.
   */
  const openReadingPlanPageInPane = (
    progress: ReadingPlanProgress | null
  ): void => {
    const page = readingPlans.readingPlanPage.peek();
    if (!page) {
      return;
    }
    readingPlans.selectedReadingPlan.value = page.item;
    void readingPlans.selectReadingPlanProgress(progress);
    // Leave the page first. Moving the address bar onto a chapter closes
    // fullscreen panes (every pane is fullscreen on a phone), so a pane
    // opened before it would close again straight away.
    tabs.leaveStaticPage();
    pendingSharedPlan.value = page.item;
  };

  // A function declaration so the `state` object above can refer to it.
  function startSharedPage(): void {
    if (playlists.playlistPage.peek()) {
      playlists.startPlaylistPage();
    } else {
      openReadingPlanPageInPane(null);
    }
  }

  // Same reason as `startSharedPage` for being a function declaration.
  function resumeSharedPage(): void {
    const place = readingPlans.readingPlanPageProgress.peek();
    if (place) {
      openReadingPlanPageInPane(place.progress);
    }
  }

  // A shared playlist or reading plan link opens on a modal describing it.
  // Start begins it; closing it any other way (Close, the header's X, the
  // backdrop) leaves for the home screen. A link to one that doesn't exist
  // opens a "not found" modal in the same place, and one that failed to load
  // opens a "try again" modal there; closing either goes home too.
  effect(() => {
    const page = sharedPage.value;
    const name = sharedPageName.value;
    const notFoundKind = sharedPageNotFoundKind.value;
    const failedKind = sharedPageLoadFailedKind.value;
    const failedTitle = sharedPageLoadFailedTitle.value;
    if (!page && failedKind && failedTitle) {
      const { t } = i18n;
      const retrying =
        failedKind === "playlist"
          ? playlists.playlistPageRetrying.value
          : readingPlans.readingPlanPageRetrying.value;
      modals.openModal({
        id: SHARED_PAGE_MODAL_ID,
        title: failedTitle,
        useCasualOSApp: false,
        content: () => (
          <SharedPageLoadFailedModalContent
            message={
              failedKind === "playlist"
                ? t("playlist-load-failed-message", {
                    defaultValue:
                      "Something went wrong loading this playlist. Check your internet connection and try again.",
                  })
                : t("reading-plan-load-failed-message", {
                    defaultValue:
                      "Something went wrong loading this reading plan. Check your internet connection and try again.",
                  })
            }
            retrying={retrying}
            onRetry={() =>
              void (failedKind === "playlist"
                ? playlists.retryPlaylistPage()
                : readingPlans.retryReadingPlanPage())
            }
            onClose={() => modals.closeModal(SHARED_PAGE_MODAL_ID)}
          />
        ),
      });
      return;
    }
    const notFoundTitle = sharedPageNotFoundTitle.value;
    if (!page && notFoundKind && notFoundTitle) {
      const { t } = i18n;
      modals.openModal({
        id: SHARED_PAGE_MODAL_ID,
        title: notFoundTitle,
        useCasualOSApp: false,
        content: () => (
          <SharedPageNotFoundModalContent
            message={
              notFoundKind === "playlist"
                ? t("playlist-not-found-message", {
                    defaultValue:
                      "This playlist doesn't exist, or it has been deleted. Check the link, or ask the person who shared it for a new one.",
                  })
                : t("reading-plan-not-found-message", {
                    defaultValue:
                      "This reading plan doesn't exist, or it has been deleted. Check the link, or ask the person who shared it for a new one.",
                  })
            }
            onClose={() => modals.closeModal(SHARED_PAGE_MODAL_ID)}
          />
        ),
      });
      return;
    }
    // A playing path (one with a step) is the playlist being read, not its
    // page, so it shows no modal.
    if (!page || !name || sharedPageStep.value != null) {
      if (
        modals.modals.peek().some((modal) => modal.id === SHARED_PAGE_MODAL_ID)
      ) {
        modals.closeModal(SHARED_PAGE_MODAL_ID);
      }
      return;
    }
    const { t } = i18n;
    const playlistPage = playlists.playlistPage.value;
    const planPage = readingPlans.readingPlanPage.value;
    let lengthLabel: string;
    let status: string | null = null;
    let actions: SharedPageModalAction[];
    if (playlistPage) {
      lengthLabel = t("playlist-page-item-count", {
        count: playlistPage.item.items.length,
        defaultValue: "{{count}} items",
      });
      actions = [
        {
          label: t("playlist-page-start", { defaultValue: "Start Playlist" }),
          primary: true,
          disabled: playlistPage.item.items.length === 0,
          onClick: startSharedPage,
        },
      ];
    } else {
      const plan = planPage?.item;
      const sessionCount = t("reading-plan-session-count-sessions", {
        count: plan?.sessions.length ?? 0,
        defaultValue: "{{count}} sessions",
      });
      // Estimated at the loaded translation's own chapter lengths when its
      // catalog is there, else at a typical chapter length.
      const books =
        selectedTab.value?.readingState.translationBooks.value?.books ?? [];
      const minutes = plan
        ? averageSessionMinutes(plan, (bookId) =>
            books.find((book) => book.id === bookId)
          )
        : null;
      lengthLabel =
        minutes != null
          ? `${sessionCount} · ${t("reading-plan-page-minutes-per-session", {
              count: minutes,
              defaultValue: "About {{count}} min per session",
            })}`
          : sessionCount;

      const startFresh = {
        label: t("reading-plan-page-start", {
          defaultValue: "Start Reading Plan",
        }),
        primary: true,
        disabled: minutes == null,
        onClick: startSharedPage,
      };
      const place = readingPlans.readingPlanPageProgress.value;
      if (!place) {
        actions = [startFresh];
      } else if (place.resumeAt == null) {
        status = t("reading-plan-page-finished", {
          defaultValue: "You've finished this plan.",
        });
        actions = [
          {
            ...startFresh,
            label: t("reading-plan-page-start-over", {
              defaultValue: "Start from beginning",
            }),
          },
        ];
      } else {
        const isDay = place.unit === "day";
        status = isDay
          ? t("reading-plan-page-on-day", {
              day: place.resumeAt,
              defaultValue: "You're on day {{day}}",
            })
          : t("reading-plan-page-on-session", {
              session: place.resumeAt,
              defaultValue: "You're on session {{session}}",
            });
        actions = [
          {
            ...startFresh,
            primary: false,
            label: t("reading-plan-page-start-over", {
              defaultValue: "Start from beginning",
            }),
          },
          {
            label: isDay
              ? t("reading-plan-page-resume-day", {
                  day: place.resumeAt,
                  defaultValue: "Resume day {{day}}",
                })
              : t("reading-plan-page-resume-session", {
                  session: place.resumeAt,
                  defaultValue: "Resume session {{session}}",
                }),
            primary: true,
            onClick: resumeSharedPage,
          },
        ];
      }
    }
    modals.openModal({
      id: SHARED_PAGE_MODAL_ID,
      title: name,
      // Rendered in place rather than in a CasualOS app iframe so the server
      // render includes it.
      useCasualOSApp: false,
      content: () => (
        <SharedPageModalContent
          heroImageUrl={page.heroImageUrl}
          authorName={page.authorName}
          description={page.description}
          lengthLabel={lengthLabel}
          status={status}
          actions={actions}
          onClose={() => modals.closeModal(SHARED_PAGE_MODAL_ID)}
        />
      ),
    });
  });

  /** Set when a visit that began on a shared page closed it for home. */
  const closedSharedPageForHome = signal(false);

  // Starting leaves the shared page (a playlist for its first step's path)
  // before the modal closes, so this only sees a close that should go home.
  // Going home is what a fresh visit to "/"
  // would do: the reader takes the address bar back, Today opens over it
  // when a visit to "/" would open it, and a visit that began on this page
  // stops counting as a content link, so the tutorial offer can appear.
  effect(() => {
    const modalOpen = modals.modals.value.some(
      (modal) => modal.id === SHARED_PAGE_MODAL_ID
    );
    const onSharedPage = !!sharedPage.peek() && sharedPageStep.peek() == null;
    if (
      modalOpen ||
      !(
        onSharedPage ||
        sharedPageNotFoundKind.peek() ||
        sharedPageLoadFailedKind.peek()
      )
    ) {
      return;
    }
    const home = new URL(navigation.initialUrl.href);
    home.pathname = `${navigation.basePath}/`;
    tabs.leaveStaticPage();
    if (todayWillAutoOpenForUrl(home, navigation.basePath)) {
      today.open();
    }
    if (openedViaSharedPage) {
      closedSharedPageForHome.value = true;
    }
  });

  // Lifts the content-link suppression only once Today's pane is actually
  // covering the reader. Lifting it in the same update as `today.open()`
  // would let the tutorial offer see a visible reader in the moment before
  // the pane opens, and show itself underneath Today.
  effect(() => {
    if (!closedSharedPageForHome.value) {
      return;
    }
    const todayPaneOpen = panes.panes.value.some(
      (pane) => pane.id === TODAY_PANE_ID
    );
    if (today.isOpen.value && !todayPaneOpen) {
      return;
    }
    openedViaContentLink.value = false;
  });

  // Settings UI language changes also select the nearest available Bible
  // translation (preferred ID → same language in catalog → LANG_META.fallback
  // → English), using existing tabs + selector state. Keep the user on the
  // same book/chapter/verse when the new translation has that book.
  i18n.setBibleTranslationApplicator(
    async (translation) => {
      const tab = selectedTab.value;
      if (tab) {
        const currentBookId = tab.readingState.bookId.peek();
        const currentChapterNumber = tab.readingState.chapterNumber.peek();
        const currentVerse =
          tab.readingState.selectedVerses
            .peek()
            .find(
              (verse) =>
                verse.bookId === currentBookId &&
                verse.chapterNumber === currentChapterNumber
            )?.verse.number ??
          tab.readingState.scrollToVerse.peek() ??
          undefined;

        let matchingBook: { id: string } | undefined;
        try {
          const books = await data.getTranslationBooks(translation.id);
          matchingBook = currentBookId
            ? books.books.find((book) => book.id === currentBookId)
            : undefined;
        } catch {
          // Catalog isn't cached yet and the fetch failed — fall through to
          // selectTranslation, which handles its own errors the way this
          // path did before position preservation was added.
        }

        if (matchingBook && currentChapterNumber != null) {
          await tab.readingState.selectTranslationAndChapter(
            translation.id,
            matchingBook.id,
            currentChapterNumber,
            currentVerse != null ? { scrollToVerse: currentVerse } : undefined
          );
        } else {
          await tab.readingState.selectTranslation(translation.id);
        }
      }
      await selector.selectTranslation(translation.id);
    },
    () => data.availableTranslations.value,
    async () => {
      if (!data.catalogLoaded.value) {
        await data.getTranslations();
      }
      return data.availableTranslations.value;
    }
  );

  setupExtensionContext(state);

  return state;
}
